/**
 * PaytableOverlay.jsx — Paytable overlay shell
 *
 * Thin wrapper: backdrop, glow decoration, header/title/separator,
 * close button, data extraction, slide assembly.
 *
 * Carousel engine lives in PaytableSlider.jsx.
 * All config constants live in paytableConfig.js.
 */

import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  useDimensions,
  ASPECT_RATIO_BREAKPOINTS,
} from "../../../../Contexts/DimensionsContext";
import { closePaytable, closeMenu } from "../../../../State/actions";
import {
  selectPaytableActive,
  selectGameFullyLoaded,
} from "../../../../State/selectors";
import {
  selectPaytable,
  selectBet,
  selectBaseBet,
  selectCurrency,
} from "../../../../State/Slices/gameSettingsSlice";

import { PixiSettings } from "../../../../pixiSettings";
import { useSecondaryToolbar } from "../../../../Hooks/useSecondaryToolbar";
import { useIosOverlayGuard } from "../../../../Hooks/useIosOverlayGuard";
import { useOverlayFade } from "../../../../Hooks/useOverlayFade";
import { getUITextureAsDataURL, getGameImageSrc } from "../../../../assets";

import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";

import SpineTextureExtractor from "../../../../utils/SpineTextureExtractor";

import PaytableSlider from "./PaytableSlider";

import {
  BASE_FONT_SIZE,
  CHROME_DEVICE_CONFIG,
  CHROME_RATIOS,
  DESC_FONT_CONFIG,
  getGlobalDescFontMultiplier,
  SLIDE_TITLES,
  SLIDE_CONTENT_SPACED_GAP_MUL,
  STANDARD_MOBILE_LANDSCAPE_TYPO,
  SMALL_MOBILE_PORTRAIT_TYPO,
  FEATURE_SLIDES,
  STACKED_LANDSCAPE_DESC_MAX_W_PCT,
  SYMBOL_SLIDE_1_CONFIG,
  SYMBOL_SLIDE_1,
  SYMBOL_SLIDE_HNW,
  SYMBOL_SLIDE_COINS,
  SYMBOL_SLIDE_ROWS,
  SYMBOL_SLIDE_FS,
  SYMBOL_SLIDE_2_CONFIG,
  SYMBOL_SLIDE_2_HEADER,
  SYMBOL_SLIDE_2,
  SYMBOL_SLIDE_2_IMG_RATIO,
  SYMBOL_SLIDE_4_CONFIG,
  getDeviceConfig,
  getImgWidth,
  getZeusSlideLayoutVal,
  getMeasureCanvas,
  measureTextHeight,
  getSymbolGroups,
  getPayValues,
  getPaytableArrowLayout,
  PAYTABLE_TUNED_LANDSCAPE_CHROME,
  isBrowserChromeShortenedPortrait,
  getPaytableTunedLandscapeSlideAreaPad,
  getPaytableTunedLandscapeSideOffset,
  getPaytableLayoutDevice,
} from "./paytableConfig";

// Feature-slide description fit-to-height safety margin — target this fraction
// of the available height so descenders aren't clipped by the container.
/**
 * How wide a symbol-grid row may be, as a share of the slide.
 *
 * Both rows fill the same width so their columns align; this pulls them in from
 * the full span so they are not flung to the edges. Safe to tune — nothing
 * measures a row, unlike .spt-grid-content, whose width feeds the symbol sizing.
 */
const GRID_ROW_MAX_WIDTH = "82%";

const FEATURE_DESC_FILL_SAFETY = 0.98;
// Portrait stacked layout — canvas measure runs slightly tall vs DOM, so allow
// a touch more fill before stepping down (reclaim pass still adds up to 1px).
const FEATURE_DESC_FILL_SAFETY_PORTRAIT = 0.99;

// The wild/scatter slide's gap between image, title and description, as a
// multiple of linePad, in portrait. Halved from 1.5, 2026-10-05: the gaps were
// eating the room the description needs, and Japanese ran into the dots.
// Read by BOTH the CSS and the slide fit, so the fit measures the gap that is
// actually drawn — it used 0.6 against a drawn 1.5 and over-filled the slide.
const SYMBOL_SECTION_GAP_MUL_PORTRAIT = 0.75;

// ── Component ──

// How much of its box a title may fill — the rest is the clear gap between
// two titles side by side (see the title fit in PaytableOverlay).
const PAYTABLE_TITLE_FILL = 0.88;
// Portrait: the smallest a description may be, as a share of the titles.
const PORTRAIT_DESC_TO_TITLE = 0.7;
// The Hold & Win symbols slide, portrait, not small phones: its titles a bit
// smaller, so the pictures (which take what the text leaves) grow (2026-11-02).
const HNW_PORTRAIT_TITLE_MUL = 0.85;
// ...and its spacing, one rule on every phone (2026-11-02, "unified, same
// ratios"): picture -> title -> text one gap, sections apart by twice it, both
// a share of the slide body's height (cqh).
const HNW_PORTRAIT_GAP_CQH = 1.2;
const HNW_PORTRAIT_SECTION_GAP_MUL = 2;
// Small mobile portrait: every paytable picture box, as a share of what it
// gets on a standard phone.
const SMALL_PORTRAIT_IMG_SCALE = 0.85;

const PaytableOverlay = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const paytableActive = useSelector(selectPaytableActive);
  const paytable = useSelector(selectPaytable);
  const bet = useSelector(selectBet);
  const baseBet = useSelector(selectBaseBet);
  const currency = useSelector(selectCurrency);

  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
    uiSymbolSize,
    isMobileSmall,
  } = useDimensions();
  const toolbar = useSecondaryToolbar();
  // Tablet uses MOBILE layout keys — only uiSymbolScale/viewport size differs.
  // Desktop adopts the standard-mobile-landscape baseline (mirrored via
  // isMirroredMobileLandscape below), so it also reads MOBILE layout keys.
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  // Wide desktop is a refinement of desktop: it inherits every desktop layout
  // branch and only overrides the values gated on isWideDesktop below. Derived
  // straight from dimensions (like useSecondaryToolbar) — the context collapses
  // all desktop widths to DEVICE.DESKTOP, so we read the width tier here.
  // Covers wide + ultra-wide (both ≥ WIDE_DESKTOP_MIN_WIDTH, 2540px).
  const isWideDesktop =
    isDesktop &&
    dimensions.width >= ASPECT_RATIO_BREAKPOINTS.WIDE_DESKTOP_MIN_WIDTH;
  const layoutDevice = isDesktop
    ? PixiSettings.DEVICE.MOBILE
    : getPaytableLayoutDevice(device);

  const { isHidden } = useIosOverlayGuard();

  // Mount + fade lifecycle — shared hook (animejs on the RAF ticker, no timers).
  // const { shouldRender, overlayRef } = useOverlayFade({
  //   active: paytableActive,
  //   isHidden,
  // });
  // ── Built once the game has loaded, and kept (2026-10-30, RULE #1) ── it
  // was built on every open (DOM, text fits, image decodes) and then waited
  // 200ms before fading in, so it opened late. Now opening is only the fade;
  // closed, it is hidden (visibility) and takes no input.
  const gameFullyLoaded = useSelector(selectGameFullyLoaded);
  const { shouldRender, overlayRef, isOpen } = useOverlayFade({
    active: paytableActive,
    isHidden,
    keepMounted: true,
    ready: gameFullyLoaded,
  });

  // Re-measure trigger. The fit effects below read container sizes from the
  // DOM, but on a drag-resize the containers lag the viewport by a frame, so a
  // synchronous measure runs against an unsettled size and never corrects
  // (fine on reload — a single settled measure). Bumping this on the frame
  // after any dimensions change forces one more measure pass once layout has
  // settled; the state-change guards make it a no-op when nothing moved.
  const [measureTick, setMeasureTick] = useState(0);
  useEffect(() => {
    if (!shouldRender) return undefined;
    const raf = requestAnimationFrame(() => setMeasureTick((t) => t + 1));
    return () => cancelAnimationFrame(raf);
  }, [shouldRender, dimensions.width, dimensions.height]);

  // ── Responsive sizing — Single chrome computation for Overlay + Slider ──
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const chrome = useMemo(() => {
    const config = getDeviceConfig(
      CHROME_DEVICE_CONFIG,
      layoutDevice,
      orientation,
    );
    const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
    return {
      title: Math.round(base * CHROME_RATIOS.title),
      subtitle: Math.round(base * CHROME_RATIOS.subtitle),
      arrow: Math.round(
        base * CHROME_RATIOS.arrow * (config.arrowScale || 1.0),
      ),
      close: Math.round(base * CHROME_RATIOS.close),
      dot: Math.round(base * CHROME_RATIOS.dot),
      separator: Math.round(base * CHROME_RATIOS.separatorHeight),
      topPad: Math.round(base * CHROME_RATIOS.topPad),
      base,
    };
  }, [layoutDevice, orientation, uiSymbolScale]);

  // ── Shell layout — standard mobile portrait aligned with GameRulesOverlay ──
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Toolbar-shortened viewport → small-mobile portrait shell. Fullscreen tall
  // phones (game height ≈ screen height) keep standard-mobile padding even when
  // visible CSS height is under SHORT_PORTRAIT_HEIGHT. See
  // isBrowserChromeShortenedPortrait in paytableConfig.js.
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const isStandardMobilePortrait =
    isStandardMobile && isPortrait && !isShortStandardPortrait;
  const isStandardMobileLandscape = isStandardMobile && !isPortrait;
  const isTabletPortrait = isTablet && isPortrait;
  const isTabletLandscape = isTablet && !isPortrait;
  const isSmallMobilePortrait =
    isPortrait && (isMobileSmall || isShortStandardPortrait);
  const isSmallMobileLandscape = isMobileSmall && !isPortrait;
  const isTunedStandardPortrait = isStandardMobilePortrait || isTabletPortrait;
  // Tablet landscape and desktop both mirror standard mobile landscape exactly
  // (0 layout diffs); desktop then tunes via desktop* config knobs.
  const isMirroredMobileLandscape =
    isStandardMobileLandscape || isTabletLandscape || isDesktop;
  const isTunedMobileLandscape =
    isMirroredMobileLandscape || isSmallMobileLandscape;
  const isTunedShellPortrait = isTunedStandardPortrait || isSmallMobilePortrait;

  const shellTopPad = isStandardMobilePortrait
    ? chrome.topPad * 7.5
    : isTabletPortrait
      ? chrome.topPad * 0.8
      : isTabletLandscape
        ? chrome.topPad * 1.4 * 6.5
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellTopPadMul
          : isDesktop
            ? chrome.topPad * 1.4
            : isTunedMobileLandscape
            ? chrome.topPad * 1.6
            : isSmallMobilePortrait
              ? chrome.topPad * 0.8
              : Math.round(chrome.topPad * 1.3);

  const shellBottomPad = isStandardMobilePortrait
    ? chrome.topPad * 7.75
    : isTabletPortrait
      ? chrome.topPad * 1.5
      : isTabletLandscape
        ? chrome.topPad * 3.3 * 3.35
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellBottomPadMul
          : isTunedMobileLandscape
          ? chrome.topPad * 3.3
          : isSmallMobilePortrait
            ? chrome.topPad * 1.5
            : isPortrait
              ? chrome.topPad * 3.8
              : chrome.topPad * 3.2;

  // Content width is a fixed multiple of the (viewport-fit) symbol size, so it
  // scales continuously with the layout — no absolute cap. uiSymbolSize is
  // already fit to the viewport by DimensionsContext (its scale is the min of
  // width-fit / height-fit / scaleCap), so a multiple of it can never overflow;
  // when the window shrinks the symbols shrink and the popup shrinks with them.
  const contentWidthSymbolMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopContentWidthSymbolMul
    : isDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopContentWidthSymbolMul
    : isSmallMobileLandscape
      ? PAYTABLE_TUNED_LANDSCAPE_CHROME.smallMobileLandscapeContentWidthSymbolMul
      : isTabletLandscape
        ? PAYTABLE_TUNED_LANDSCAPE_CHROME.tabletLandscapeContentWidthSymbolMul
        : PAYTABLE_TUNED_LANDSCAPE_CHROME.standardMobileLandscapeContentWidthSymbolMul;
  const tunedMobileLandscapeShellWidth = isTunedMobileLandscape
    ? Math.round(uiSymbolSize * contentWidthSymbolMul)
    : null;

  const tabletPortraitContentWidth = isTabletPortrait
    ? dimensions.width * 0.5
    : null;

  const shellContentWidth = isStandardMobilePortrait
    ? dimensions.width * 0.75
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isSmallMobilePortrait
        ? dimensions.width * 0.8
        : (tunedMobileLandscapeShellWidth ?? undefined);

  const separatorLineWidth = isStandardMobilePortrait
    ? Math.min(chrome.title * 12, dimensions.width * 0.95)
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isTunedMobileLandscape
        ? tunedMobileLandscapeShellWidth
        : isPortrait
          ? Math.min(chrome.title * 12, dimensions.width * 0.92)
          : Math.min(chrome.title * 16, dimensions.width * 0.65);

  const separatorHeight = isTunedStandardPortrait
    ? Math.round(chrome.separator * 1.25)
    : isWideDesktop
      ? Math.round(
          chrome.separator *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSeparatorMul,
        )
      : isDesktop
      ? Math.round(chrome.separator * 1.0)
      : isTunedMobileLandscape
        ? Math.round(chrome.separator * 1.2)
        : chrome.separator;

  const closeButtonSize = isTunedMobileLandscape
    ? Math.round(chrome.close * PAYTABLE_TUNED_LANDSCAPE_CHROME.closeMul)
    : chrome.close;

  const tunedLandscapeSlideAreaPad =
    isTunedMobileLandscape && shellContentWidth != null
      ? getPaytableTunedLandscapeSlideAreaPad(
          dimensions.width,
          shellContentWidth,
        )
      : null;
  const tunedLandscapeChromeSideOffset =
    tunedLandscapeSlideAreaPad != null
      ? getPaytableTunedLandscapeSideOffset(chrome, tunedLandscapeSlideAreaPad)
      : null;

  const closeButtonRight = isPortrait
    ? Math.max(6, Math.round(chrome.close * 0.12))
    : tunedLandscapeChromeSideOffset != null
      ? Math.max(4, tunedLandscapeChromeSideOffset - Math.round(chrome.close * 0.35))
      : shellContentWidth != null
        ? Math.max(
            4,
            Math.round((dimensions.width - shellContentWidth) / 2) -
              Math.round(chrome.close * 0.8),
          )
        : Math.round(
            (dimensions.width - separatorLineWidth) / 2 - chrome.close * 3.0,
          );

  const linePad = Math.round(chrome.title * 0.5);
  const slideContentGap = isTunedMobileLandscape
    ? Math.round(linePad * 1.5)
    : linePad;
  const slideContentSpacedGapMul = isTunedStandardPortrait
    ? SLIDE_CONTENT_SPACED_GAP_MUL.mobilePortrait
    : isSmallMobilePortrait
      ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileSmallPortrait
      : isTunedMobileLandscape
        ? SLIDE_CONTENT_SPACED_GAP_MUL.mobileLandscape
        : SLIDE_CONTENT_SPACED_GAP_MUL.default;
  const slideContentSpacedGap = isWideDesktop
    ? Math.round(
        linePad * PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideContentSpacedGapMul,
      )
    : isDesktop
    ? Math.round(linePad * 1.0)
    : isTunedMobileLandscape
      ? slideContentGap
      : Math.round(linePad * slideContentSpacedGapMul);
  const separatorMarginTop = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : 0;
  const separatorMarginBottom = isTunedStandardPortrait
    ? Math.round(linePad * 0.6)
    : isSmallMobilePortrait
      ? Math.round(linePad * 0.6)
      : isTunedMobileLandscape
        ? Math.round(linePad * 0.6)
        : 0;
  const footerMarginTop = isTunedMobileLandscape
    ? Math.round(linePad * 1.0)
    : separatorMarginBottom;
  const headerPaddingBottom = isTunedShellPortrait
    ? 0
    : Math.round(linePad * 0.25);

  const landscapeUniformTypo = isTunedMobileLandscape
    ? STANDARD_MOBILE_LANDSCAPE_TYPO
    : null;
  const landscapeFixedDescFont = landscapeUniformTypo?.fixedDescFont ?? false;
  const uniformDescFontMul = landscapeUniformTypo?.descFontMul ?? null;
  const uniformFeatureSubtitleMul =
    landscapeUniformTypo?.featureSubtitleMul ?? null;

  // Global description font multiplier — one number for the current device/
  // orientation tier + locale, layered on top of every slide's own descFontMul.
  // See getGlobalDescFontMultiplier in paytableConfig.js for the full rationale.
  const descFontTierKey = isSmallMobilePortrait
    ? "mobileSmallPortrait"
    : isSmallMobileLandscape
      ? "mobileSmallLandscape"
      : isStandardMobilePortrait
        ? "mobilePortrait"
        : isStandardMobileLandscape
          ? "mobileLandscape"
          : isTabletPortrait
            ? "tabletPortrait"
            : isTabletLandscape
              ? "tabletLandscape"
              : isWideDesktop
                ? "wideDesktop"
                : "desktop";
  const globalDescFontMultiplier = useMemo(
    () => getGlobalDescFontMultiplier(descFontTierKey, i18n.language),
    [descFontTierKey, i18n.language],
  );

  const getSlideDescFontMul = useCallback(
    (defaultMul = 0.8) =>
      (uniformDescFontMul ?? defaultMul) * globalDescFontMultiplier,
    [uniformDescFontMul, globalDescFontMultiplier],
  );
  const smallMobilePortraitSubtitleMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.subtitleFontMul
    : 1;
  const getUniformFeatureSubtitleH = useCallback(
    (measureMul = 1) =>
      uniformFeatureSubtitleMul != null
        ? Math.round(
            chrome.subtitle * 0.7 * uniformFeatureSubtitleMul * measureMul,
          )
        : Math.round(
            chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul * measureMul,
          ),
    [
      uniformFeatureSubtitleMul,
      chrome.subtitle,
      smallMobilePortraitSubtitleMul,
    ],
  );

  const headerTitleFontSize = isTunedStandardPortrait
    ? Math.round(chrome.title * 0.94)
    : isWideDesktop
      ? Math.round(
          chrome.title *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopHeaderTitleMul,
        )
      : isDesktop
      ? Math.round(
          chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopHeaderTitleMul,
        )
      : isTunedMobileLandscape
        ? Math.round(
            chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.headerTitleMul,
          )
        : chrome.title;

  const slideTitleFontSize = isWideDesktop
    ? Math.round(
        chrome.subtitle *
          PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopSlideTitleMul,
      )
    : isDesktop
    ? Math.round(
        chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopSlideTitleMul,
      )
    : isTunedMobileLandscape
      ? Math.round(
          chrome.subtitle * PAYTABLE_TUNED_LANDSCAPE_CHROME.slideTitleMul,
        )
      : chrome.subtitle;

  const featureSubtitleFontSize = isTunedMobileLandscape
    ? getUniformFeatureSubtitleH()
    : Math.round(chrome.subtitle * 0.7 * smallMobilePortraitSubtitleMul);

  const glowTop = isTunedStandardPortrait ? "-12%" : "-8%";
  const glowWidth = isTunedStandardPortrait ? "150%" : "120%";

  const arrowLayout = getPaytableArrowLayout(chrome);
  const arrowSafeZone = arrowLayout.safeGutter * 2;

  const handleClose = () => {
    soundManager.play(SOUND_NAME.button_click);
    dispatch(closePaytable());
    dispatch(closeMenu());
  };

  // ── The header title can NEVER reach the X ── (from 3-orbs-of-zeus
  // 0af85ce8, 2026-10-05)
  //
  // The X is absolutely placed over the title row, so a long translation
  // ("Tabla de pagos") ran underneath it. Two halves:
  //
  //   RESERVE  the title is padded on BOTH sides by the X's footprint, so it
  //            stays centred on the row and its box ends before the X starts.
  //   FIT      the text is measured and, if wider than that box, scaled down
  //            with a transform — the replay modal's stats-row rule: measure
  //            offsetWidth (layout, untouched by the transform), never the rect.
  const titleSideReserve = Math.max(
    0,
    closeButtonRight + closeButtonSize + Math.round(closeButtonSize * 0.25),
  );
  const titleRef = useRef(null);
  const titleTextRef = useRef(null);
  const [titleScale, setTitleScale] = useState(1);
  useLayoutEffect(() => {
    const box = titleRef.current;
    const text = titleTextRef.current;
    if (!box || !text) return;
    const style = window.getComputedStyle(box);
    const available =
      box.clientWidth -
      (parseFloat(style.paddingLeft) || 0) -
      (parseFloat(style.paddingRight) || 0);
    const needed = text.offsetWidth;
    if (!(available > 0) || !(needed > 0)) return;
    // A hair under 1 so a rounding difference cannot leave it touching.
    setTitleScale(needed > available ? (available / needed) * 0.99 : 1);
  }, [
    // The overlay mounts a commit after it opens — key on the mount flag.
    shouldRender,
    t,
    headerTitleFontSize,
    titleSideReserve,
    dimensions.width,
  ]);

  // ── Build slides array ──
  // Each entry is a render function () => JSX. The slider wraps each in <PaytableSlide>.

  // Extract all spine data URLs (used by slide 3 for mid/low pay symbols)
  const spineDataURLs = useMemo(() => {
    return SpineTextureExtractor.getDataURLs() || {};
  // }, [shouldRender]);
  // re-read on open too: cached by then, costs nothing
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, isOpen]);

  // Build symbol grid data for slide 3: mid pay (hp+mp = 4) + low pay (5)
  const symbolGridData = useMemo(() => {
    const groups = getSymbolGroups();
    const buildSection = (symbols) =>
      symbols.map((s) => ({
        id: s.id,
        name: s.name,
        imgSrc: spineDataURLs[s.name]?.src || null,
        imgW: spineDataURLs[s.name]?.width || 0,
        imgH: spineDataURLs[s.name]?.height || 0,
        payValues: getPayValues(paytable, s.id, bet, baseBet),
      }));

    return {
      midPay: buildSection([...groups.hp, ...groups.mp]),
      lowPay: buildSection(groups.lp),
    };
  }, [spineDataURLs, paytable, bet, baseBet]);

  // ── Game image cache — extract all data URLs once when overlay mounts ──
  // Same pattern as primary PaytableSection.symbolImages: useMemo runs once,
  // all slides read from the map — zero canvas work during slider transitions.
  const gameImages = useMemo(() => {
    if (!shouldRender) return {};
    const names = [
      // Feature slides — composited from split pot/coins images
      // "paylines_portrait", — landscape serves both (2026-10-13)
      "paylines_landscape",
      "glow",
      // ── Mad Labs paytable symbols ──
      //
      // This list is the reason an image can be registered, loaded and still
      // draw nothing: the map is built from these names ONLY, so a slide asking
      // for one that is missing gets `undefined` and its `{imgSrc && …}` guard
      // renders an empty box. No error, no warning — the exact failure `wild`
      // and `scatter` hit.
      //
      // Anything a slide names in `assetName` has to appear here too.
      // "wild",
      // "scatter",
      // (wild / scatter: now their spine pictures — SYMBOL_SLIDE_1.symbolName)
      // "gamble", — never registered; its file deleted (2026-09-30)
      // The intro popup's three feature images, shared with the paytable.
      "startpage_image_1",
      "startpage_image_2",
      "startpage_image_3",
      // Free Spins and Royal Jackpot art (2026-10-02).
      "startpage_image_4",
      "startpage_image_5",
      "paytable_royal_jackpot",
      // Mad Labs' paytable art — removed with its files (2026-09-30):
      // "paytable_h1",
      // "paytable_m1",
      // "paytable_m2",
      // "paytable_m3",
      // "paytable_l1",
      // "paytable_l2",
      // "paytable_l3",
      // "paytable_l4",
    ];
    const result = {};
    for (const name of names) {
      result[name] = getGameImageSrc(name);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender]);

  // Payline image — picked from cache based on current orientation
  // const paylineImgSrc =
  //   gameImages[isPortrait ? "paylines_portrait" : "paylines_landscape"] || "";
  // One picture for both orientations (Vladimir, 2026-10-13): Cleopatra's
  // paylines_landscape is near-square and fits portrait too.
  const paylineImgSrc = gameImages.paylines_landscape || "";

  // ── Dynamic font size for feature descriptions ──
  // Single path for all devices — ratios from DESC_FONT_CONFIG.
  const fontCfg = getDeviceConfig(DESC_FONT_CONFIG, layoutDevice, orientation);
  const smallMobilePortraitDescMul = isSmallMobilePortrait
    ? SMALL_MOBILE_PORTRAIT_TYPO.descFontMul
    : 1;
  const descBaseFontSize = Math.round(
    chrome.base * fontCfg.baseMul * smallMobilePortraitDescMul,
  );
  // Small-mobile-landscape description shrink — shared by the feature slides
  // (baked into uniformDescFontSize) and the symbol slides (wild, slide 1) so
  // every description renders at the same size on this breakpoint.
  const smallMobileLandscapeDescMul = isSmallMobileLandscape
    ? (landscapeUniformTypo?.smallMobileLandscapeDescFontMul ?? 1)
    : 1;
  const uniformDescFontSize = landscapeUniformTypo
    ? Math.round(
        descBaseFontSize *
          landscapeUniformTypo.descFontMul *
          smallMobileLandscapeDescMul,
      )
    : null;
  // ── Refs for measuring feature slide body containers (portrait + landscape) ──
  const featureBodyRefs = useRef([]);
  const [featureFontSizes, setFeatureFontSizes] = useState(null);

  // ── Dynamic feature text sizing — measures actual container from DOM on every resize ──
  // Uses useLayoutEffect to read real container dimensions from refs, then steps down
  // font size until text fits. Runs before paint so there's no visual flash.
  useLayoutEffect(() => {
    if (!shouldRender) {
      setFeatureFontSizes((prev) => (prev === null ? prev : null));
      return;
    }

    const hasRefs = featureBodyRefs.current.some((el) => el != null);
    if (!hasRefs) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    const newSizes = FEATURE_SLIDES.map((f, idx) => {
      const bodyEl = featureBodyRefs.current[idx];
      const baseFont = Math.round(
        descBaseFontSize * getSlideDescFontMul(f.descFontMul),
      );

      if (!bodyEl) {
        return {
          fontSize: isPortrait
            ? baseFont
            : landscapeFixedDescFont
              ? uniformDescFontSize
              : descBaseFontSize,
          descWidths: [],
        };
      }

      // Small mobile landscape keeps the fixed uniform desc size (its
      // uniformDescFontSize already bakes in the extra shrink). Standard
      // mobile landscape, tablet landscape, and desktop fall through to the
      // measure-and-fit path below so long translations can't overflow.
      if (!isPortrait && isSmallMobileLandscape && uniformDescFontSize != null) {
        return { fontSize: uniformDescFontSize, descWidths: [] };
      }

      const descText = t(f.desc);
      let textW;
      let textAvailH;

      // ── Which LAYOUT, not which orientation ──
      //
      // This used to test isPortrait, which was the same question until the
      // column was opened up to landscape. A landscape column then fell into
      // the side-by-side branch below and measured `.spt-feature-text-wrap` —
      // an element the column does not render — so it silently used the
      // half-width fallback and sized the text for a box it does not have.
      const isStackedLayout = isPortrait || f.stackedAlways === true;

      if (isStackedLayout) {
        // Stacked layout: subtitle → image → description (measure image from DOM).
        const stackedBodyEl = bodyEl.querySelector(".spt-feature-stacked-body");
        const imgWrapEl = stackedBodyEl?.querySelector(".spt-feature-img-wrap");
        const imageH = imgWrapEl?.offsetHeight ?? 0;
        const stackedBodyH = stackedBodyEl?.clientHeight ?? bodyEl.clientHeight;
        // The landscape column caps the description and has no gaps; portrait
        // fills its width and keeps linePad between the image and the text.
        textW = isPortrait
          ? bodyEl.clientWidth
          : Math.round(
              bodyEl.clientWidth * (STACKED_LANDSCAPE_DESC_MAX_W_PCT / 100),
            );
        textAvailH = Math.max(
          0,
          stackedBodyH - imageH - (isPortrait ? lp : 0),
        );
      } else {
        // Side-by-side layout: image left, subtitle + description right.
        const bodyH = bodyEl.clientHeight;
        const bodyW = bodyEl.clientWidth;
        const textWrapEl = bodyEl.querySelector(".spt-feature-text-wrap");
        const descPad = chrome.base * 0.3;
        textW = textWrapEl
          ? textWrapEl.clientWidth
          : (bodyW * 0.5 - descPad * 2) * 0.85;

        const subtitleEl = bodyEl.querySelector(".spt-feature-subtitle");
        const subtitleH =
          subtitleEl?.offsetHeight ??
          Math.round(getUniformFeatureSubtitleH(1.2));
        const paddingTop = isTunedMobileLandscape
          ? 0
          : Math.round(lp * (f.textPadMul != null ? f.textPadMul : 1.5));
        const textWrapGap = isTunedMobileLandscape
          ? Math.round(lp * 0.4)
          : Math.round(lp * 0.5);
        textAvailH = Math.max(
          0,
          bodyH - subtitleH - paddingTop - textWrapGap,
        );
      }

      // Fit-to-height (same approach as wild / coin slides): start at the shared
      // base size, then step down until the description fits.
      const fillSafety = isPortrait
        ? FEATURE_DESC_FILL_SAFETY_PORTRAIT
        : FEATURE_DESC_FILL_SAFETY;
      const maxTextH = textAvailH * fillSafety;
      let fontSize = baseFont;
      let designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      while (designTextH > maxTextH && fontSize > 6) {
        fontSize -= 1;
        designTextH = measureTextHeight(ctx, descText, fontSize, textW);
      }
      // Portrait: canvas wrapping is slightly taller than the DOM — reclaim 1px
      // when the full container height can still fit it (avoids over-shrinking).
      if (isPortrait && fontSize < baseFont) {
        const bumped = fontSize + 1;
        const bumpedH = measureTextHeight(ctx, descText, bumped, textW);
        if (bumpedH <= textAvailH) {
          fontSize = bumped;
        }
      }

      return { fontSize, descWidths: [] };
    });

    // Only update state if values actually changed (prevents infinite re-render loop)
    setFeatureFontSizes((prev) => {
      if (
        prev &&
        prev.length === newSizes.length &&
        prev.every(
          (v, i) =>
            v.fontSize === newSizes[i].fontSize &&
            JSON.stringify(v.descWidths) ===
              JSON.stringify(newSizes[i].descWidths),
        )
      ) {
        return prev;
      }
      return newSizes;
    });
  }, [
    shouldRender,
    isPortrait,
    isSmallMobileLandscape,
    isTunedMobileLandscape,
    dimensions,
    descBaseFontSize,
    uniformDescFontSize,
    landscapeFixedDescFont,
    chrome,
    t,
    uniformFeatureSubtitleMul,
    getUniformFeatureSubtitleH,
    getSlideDescFontMul,
    measureTick,
  ]);

  // ── Refs for measuring slide content containers ──
  const slide1SectionsRef = useRef(null);
  // The Hold & Win symbols slide (index 1) — measured like slide 1.
  const hnwSectionsRef = useRef(null);
  const zeusContentRef = useRef(null);
  const gridContentRef = useRef(null);
  const [slideSizes, setSlideSizes] = useState(null);

  // ── Dynamic sizing for slides 0–3 — measures actual containers from DOM on every resize ──
  useLayoutEffect(() => {
    if (!shouldRender) return;

    const canvas = getMeasureCanvas();
    const ctx = canvas.getContext("2d");
    const lp = Math.round(chrome.title * 0.5);

    // ── Coin + Jackpot slide (slide 1) — uniform scale, integer pixels ──
    let slide1 = {
      imgH: 100,
      fontSize: Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      ),
    };
    if (slide1SectionsRef.current) {
      const el = slide1SectionsRef.current;
      const containerH = el.clientHeight;
      const containerW = el.clientWidth;
      const numSections = SYMBOL_SLIDE_1.length;

      let sectionH, textW;
      if (isPortrait) {
        const betweenGaps = Math.round(lp * 1.6) * (numSections - 1);
        sectionH = (containerH - betweenGaps) / numSections;
        textW = containerW * 0.75;
      } else if (isTunedMobileLandscape) {
        sectionH = containerH;
        const sectionGap = lp;
        textW = (containerW - sectionGap) / numSections;
      } else {
        sectionH = containerH;
        const descPad = chrome.base * 0.3 * 2;
        textW = (containerW / numSections - descPad) * 0.6;
      }

      const imgFraction = isPortrait
        ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.portrait ?? 0.5)
        : isTunedMobileLandscape
          ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38) *
            (SYMBOL_SLIDE_1_CONFIG.standardMobileLandscapeImgFractionMul ?? 1)
          : (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38);
      const designImgH = Math.round(sectionH * imgFraction);
      const designSubtitleH = getUniformFeatureSubtitleH();
      const designDescFont = Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      );
      // const gap = Math.round(lp * 0.6);
      const gap = Math.round(
        lp * (isPortrait ? SYMBOL_SECTION_GAP_MUL_PORTRAIT : 0.6),
      );

      const slide1ImgScaleMul = SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
      const maxDesignImgH = Math.max(
        ...SYMBOL_SLIDE_1.map((s) => {
          const sc =
            ((typeof s.imgScale === "object"
              ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
              : s.imgScale) || 1) * slide1ImgScaleMul;
          return Math.round(designImgH * sc);
        }),
      );

      // Only image + description scale; subtitle + gaps are fixed. Fit the
      // scalable parts into the height remaining after that fixed overhead
      // (see the Wild slide above for why scaling the whole stack overflows).
      const fixedOverhead = designSubtitleH * 1.3 + gap * 2;
      const perSection = SYMBOL_SLIDE_1.map((s) => {
        const textH = measureTextHeight(ctx, t(s.desc), designDescFont, textW);
        const scalableH = maxDesignImgH + textH;
        return isSmallMobileLandscape
          ? Math.min(1, sectionH / (scalableH + fixedOverhead))
          : Math.min(
              1,
              Math.max(0, sectionH * FEATURE_DESC_FILL_SAFETY - fixedOverhead) /
                Math.max(1, scalableH),
            );
      });
      const scale = Math.min(...perSection);

      const scaledFontSize = isSmallMobileLandscape
        ? Math.round(designDescFont * smallMobileLandscapeDescMul)
        : Math.round(designDescFont * scale);
      slide1 = {
        imgH: Math.round(designImgH * scale),
        fontSize: scaledFontSize,
        ...(isTunedMobileLandscape
          ? {}
          : {
              descWidths: [
                Math.round(scaledFontSize * 18),
                Math.round(scaledFontSize * 22),
              ],
            }),
      };
    }

    // ── Hold & Win symbols slide (index 1) — slide 1's sizing, for its items ──
    // A copy of the block above with the list and the container swapped, so
    // slide 1 stays exactly as it was tuned. Three sections instead of two.
    let slideHnw = {
      imgH: 100,
      fontSize: Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      ),
    };
    if (hnwSectionsRef.current) {
      const el = hnwSectionsRef.current;
      const containerH = el.clientHeight;
      const containerW = el.clientWidth;
      const numSections = SYMBOL_SLIDE_HNW.length;

      let sectionH, textW;
      if (isPortrait) {
        const betweenGaps = Math.round(lp * 1.6) * (numSections - 1);
        sectionH = (containerH - betweenGaps) / numSections;
        textW = containerW * 0.75;
      } else if (isTunedMobileLandscape) {
        sectionH = containerH;
        const sectionGap = lp;
        textW = (containerW - sectionGap) / numSections;
      } else {
        sectionH = containerH;
        const descPad = chrome.base * 0.3 * 2;
        textW = (containerW / numSections - descPad) * 0.6;
      }

      const imgFraction = isPortrait
        ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.portrait ?? 0.5)
        : isTunedMobileLandscape
          ? (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38) *
            (SYMBOL_SLIDE_1_CONFIG.standardMobileLandscapeImgFractionMul ?? 1)
          : (SYMBOL_SLIDE_1_CONFIG.imgFraction?.landscape ?? 0.38);
      const designImgH = Math.round(sectionH * imgFraction);
      const designSubtitleH = getUniformFeatureSubtitleH();
      const designDescFont = Math.round(
        descBaseFontSize *
          getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
      );
      const gap = Math.round(lp * 0.6);

      const hnwImgScaleMul = SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
      const maxDesignImgH = Math.max(
        ...SYMBOL_SLIDE_HNW.map((s) => {
          const sc =
            ((typeof s.imgScale === "object"
              ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
              : s.imgScale) || 1) * hnwImgScaleMul;
          return Math.round(designImgH * sc);
        }),
      );

      const fixedOverhead = designSubtitleH * 1.3 + gap * 2;
      const perSection = SYMBOL_SLIDE_HNW.map((s) => {
        const textH = measureTextHeight(ctx, t(s.desc), designDescFont, textW);
        const scalableH = maxDesignImgH + textH;
        return isSmallMobileLandscape
          ? Math.min(1, sectionH / (scalableH + fixedOverhead))
          : Math.min(
              1,
              Math.max(0, sectionH * FEATURE_DESC_FILL_SAFETY - fixedOverhead) /
                Math.max(1, scalableH),
            );
      });
      const scale = Math.min(...perSection);

      const scaledFontSize = isSmallMobileLandscape
        ? Math.round(designDescFont * smallMobileLandscapeDescMul)
        : Math.round(designDescFont * scale);
      slideHnw = {
        imgH: Math.round(designImgH * scale),
        fontSize: scaledFontSize,
        ...(isTunedMobileLandscape
          ? {}
          : {
              descWidths: SYMBOL_SLIDE_HNW.map(() => Math.round(scaledFontSize * 18)),
            }),
      };
    }

    // ── Zeus Symbols slide (slide 2) — integer pixels, font-stepping ──
    // Layout: image LEFT + text RIGHT (horizontal), so image doesn't scale with font.
    const slide2DescFontMul = getSlideDescFontMul(
      SYMBOL_SLIDE_2_CONFIG.descFontMul,
    );
    const slide2DescMultipliers =
      SYMBOL_SLIDE_2_CONFIG.descWidthMultipliers ?? [15, 15, 15];

    let slide2 = {
      imgMaxH: 80,
      imgW: 100,
      fontSize: Math.round(descBaseFontSize * slide2DescFontMul),
      headerDescFontSize: Math.round(descBaseFontSize * slide2DescFontMul),
      sectionH: 100,
      headerDescWidth: undefined,
      descWidths: [],
    };
    if (zeusContentRef.current) {
      const el = zeusContentRef.current;
      const availH = el.clientHeight;
      const availW = el.clientWidth;
      const numSections = SYMBOL_SLIDE_2.length;

      // Measure the header area (subtitle + desc) that's already rendered above the sections
      const subtitleEl = el.querySelector(".spt-feature-subtitle");
      const headerDescEl = el.querySelector(".spt-zeus-header-desc");
      const zeusLayoutCtx = {
        isStandardMobilePortrait: isTunedStandardPortrait,
        isSmallMobilePortrait,
      };
      const zeusContentSizingGap = Math.round(
        lp *
          (isPortrait
            ? (SYMBOL_SLIDE_2_CONFIG.contentGapMul ?? 0.8)
            : (SYMBOL_SLIDE_2_CONFIG.contentSizingGapMul ??
              SYMBOL_SLIDE_2_CONFIG.contentGapMul ??
              0.8)),
      );
      const zeusSectionRowSizingGap = Math.round(
        lp *
          (isPortrait
            ? (SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ?? 0.8)
            : (SYMBOL_SLIDE_2_CONFIG.sectionRowSizingGapMul ??
              SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ??
              0.8)),
      );
      const zeusInnerGap = Math.round(
        lp * (SYMBOL_SLIDE_2_CONFIG.sectionInnerGapMul ?? 0.8),
      );
      const imgHeightMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.imgHeightMul,
        zeusLayoutCtx,
        0.65,
      );
      const maxTextHeightMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.maxTextHeightMul,
        zeusLayoutCtx,
        0.85,
      );
      const textMeasureWidthMul = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.textMeasureWidthMul,
        zeusLayoutCtx,
        0.8,
      );
      const portraitFixedFont = isPortrait
        ? getZeusSlideLayoutVal(
            SYMBOL_SLIDE_2_CONFIG.portraitFixedFont,
            zeusLayoutCtx,
            true,
          )
        : false;
      const portraitFontScale = getZeusSlideLayoutVal(
        SYMBOL_SLIDE_2_CONFIG.portraitFontScale,
        zeusLayoutCtx,
        1,
      );
      const designFontSize = Math.round(descBaseFontSize * slide2DescFontMul);
      const headerDescDesignSize = Math.round(
        designFontSize * (portraitFixedFont ? portraitFontScale : 1),
      );

      // Available height for the sections grid = total minus header elements + content gaps
      const subtitleH =
        subtitleEl?.offsetHeight || getUniformFeatureSubtitleH();
      const headerDescH = portraitFixedFont
        ? measureTextHeight(
            ctx,
            t(SYMBOL_SLIDE_2_HEADER.desc),
            headerDescDesignSize,
            availW,
          )
        : headerDescEl?.offsetHeight || 0;
      const headerOverhead = subtitleH + headerDescH + zeusContentSizingGap * 2;
      const sectionsAvailH = Math.max(0, availH - headerOverhead);

      let sectionH, sectionW;
      const imgRatio = getImgWidth(
        SYMBOL_SLIDE_2_IMG_RATIO,
        layoutDevice,
        isPortrait,
      );
      const innerGap = zeusInnerGap;

      if (isPortrait) {
        const betweenGaps = zeusSectionRowSizingGap * (numSections - 1);
        sectionH = Math.round(
          ((sectionsAvailH - betweenGaps) / numSections) * 0.98,
        );
        sectionW = availW;

        const imgW = Math.round(sectionW * imgRatio);
        const textW = (sectionW - imgW - innerGap) * textMeasureWidthMul;
        const maxTextH = sectionH * maxTextHeightMul;
        const perSection = SYMBOL_SLIDE_2.map((s) => {
          const textH = measureTextHeight(
            ctx,
            t(s.desc),
            designFontSize,
            textW,
          );
          return Math.min(1, maxTextH / textH);
        });
        const fitScale = Math.min(...perSection);
        const fontSize = portraitFixedFont
          ? Math.round(designFontSize * portraitFontScale)
          : Math.round(designFontSize * fitScale);
        const imgMaxH = Math.max(0, Math.round(sectionH * imgHeightMul));
        const headerDescFontSize = Math.round(
          designFontSize * (portraitFixedFont ? portraitFontScale : fitScale),
        );

        slide2 = {
          imgMaxH,
          imgW,
          fontSize,
          headerDescFontSize,
          sectionH,
          headerDescWidth: undefined,
          descWidths: slide2DescMultipliers.map((m) =>
            Math.round(fontSize * m),
          ),
        };
      } else {
        // Landscape: uniform-scale a fixed design. The content is laid out once
        // at SYMBOL_SLIDE_2_CONFIG.landscapeDesign px sizes (fixed font + fixed
        // widths → identical line-breaks on every landscape device), then the
        // whole block is transform:scale()'d to fit the container. Natural size
        // is read via offsetWidth/Height, which ignore the CSS transform, so the
        // measurement is stable regardless of the scale currently applied.
        const design = SYMBOL_SLIDE_2_CONFIG.landscapeDesign;
        const contentEl = el.querySelector(".spt-zeus-content");
        // The white subtitle is an unscaled sibling above the scaled block, so
        // reserve its height (+ the standard subtitle gap, matching
        // featureTextWrapGap = lp*0.4) before fitting the rest.
        const subH = subtitleEl?.offsetHeight || 0;
        const wrapGap = Math.round(lp * 0.4);
        const contentAvailH = Math.max(0, availH - subH - wrapGap);
        const naturalW = contentEl?.offsetWidth || design.width;
        const naturalH = contentEl?.offsetHeight || 0;
        const scaleCap = design.scaleCap ?? 2.0;
        const rawScale =
          naturalH > 0
            ? Math.min(availW / naturalW, contentAvailH / naturalH)
            : 1;
        const scale = Math.max(0.1, Math.min(scaleCap, rawScale));
        slide2 = { scale };
      }
    }

    // ── Regular Grid slide (slide 3) — integer pixels, continuous scale ──
    let slide3 = {
      imgMaxH: 60,
      lpImgMaxH: 50,
      pvFontSize: 12,
    };
    if (gridContentRef.current) {
      const el = gridContentRef.current;
      const availH = el.clientHeight;
      const availW = el.clientWidth;

      if (isPortrait) {
        // const blockGap = Math.round(lp * 0.3);
        // const portraitRows = 4;
        // const totalGaps = blockGap * (portraitRows - 1);
        // ── Budget what the grid really DRAWS ── (Vladimir, 2026-09-30: the
        // grid ran past its container into the dots). The budget above was
        // lp × 0.3 per gap, but the rows are drawn with the CSS row gap
        // (gridPortraitRowGap), the block gap before the last row, and each
        // cell's picture-to-values gap — none of it counted. Read them off the
        // rendered elements, and count the rows from the data.
        const block2 = el.querySelector(".spt-grid-block-2col");
        const firstCell = el.querySelector(".spt-grid-cell");
        const rowGapPx = block2
          ? parseFloat(getComputedStyle(block2).rowGap) || 0
          : Math.round(lp * 0.3);
        const contentGapPx =
          parseFloat(getComputedStyle(el).rowGap) || rowGapPx;
        const cellGapPx = firstCell
          ? parseFloat(getComputedStyle(firstCell).rowGap) || 0
          : 0;
        const twoColCount =
          symbolGridData.midPay.length + Math.min(2, symbolGridData.lowPay.length);
        const twoColRows = Math.ceil(twoColCount / 2);
        const threeColRows = symbolGridData.lowPay.length > 2 ? 1 : 0;
        const portraitRows = Math.max(1, twoColRows + threeColRows);
        const totalGaps =
          rowGapPx * Math.max(0, twoColRows - 1) +
          (threeColRows ? contentGapPx : 0);
        const rowH = Math.round(
          Math.max(0, (availH - totalGaps) / portraitRows),
        );

        const basePvFont = Math.round(chrome.base * 0.82);
        const pvFontSize =
          basePvFont + (SYMBOL_SLIDE_4_CONFIG.portraitPvFontSizeOffset ?? 0);
        // ── How many pay lines a row actually draws ──
        //
        // This was hardcoded to 3, and the high pays carry FOUR (5/4/3/2 of a
        // kind) while everything else carries three. So the tallest row was
        // budgeted one line short, and with four rows stacked the slide ran
        // past the pagination dots and clipped the bottom one.
        //
        // Taken from the data instead: every row is laid out at the same height
        // (`imgMaxH` is shared), so the budget has to cover the WORST row or
        // the grid cannot fit by construction. Rows with fewer lines simply get
        // a little more air, which is the safe direction to be wrong in.
        const maxPvLines = Math.max(
          1,
          ...symbolGridData.midPay.map((sym) => sym.payValues?.length ?? 0),
          ...symbolGridData.lowPay.map((sym) => sym.payValues?.length ?? 0),
        );
        // const pvBlockH = Math.round(pvFontSize * 1.2) * maxPvLines;
        // The pay-value line's real height per px of font (its rendered line
        // box — "normal" line-height is not 1.2), measured off a rendered one.
        const pvEl = el.querySelector(".spt-grid-pv");
        const pvElFont = pvEl ? parseFloat(getComputedStyle(pvEl).fontSize) : 0;
        const pvLineRatio =
          pvEl && pvElFont > 0 ? pvEl.getBoundingClientRect().height / pvElFont : 1.4;
        const pvBlockH = Math.ceil(pvFontSize * pvLineRatio * maxPvLines);
        // const imgMaxH = Math.round(Math.max(0, rowH - pvBlockH));
        const imgMaxH = Math.floor(Math.max(0, rowH - pvBlockH - cellGapPx));
        // ── One size for BOTH rows ──
        //
        // The low-pay row used to be 65% of the mid-pay one, which read as two
        // different grids on one slide. They are the same kind of thing and are
        // now drawn the same size.
        //
        // The width fit still protects it: each row is separately capped to the
        // space it has (see widthScaleForRow in the landscape branch), so the
        // three-column block shrinks on its own if this height is too generous
        // for it — equal, and still inside the slide.
        const lpImgMaxH = imgMaxH;

        slide3 = {
          imgMaxH,
          lpImgMaxH,
          pvFontSize,
        };
      } else {
        const sectionGap = lp;
        const cellInternalGap = Math.round(lp * 0.1);
        const pvLineHeight = 1.3;
        const mpMaxLines = 4;
        const lpMaxLines = 3;

        const designPvFont = Math.round(chrome.base * 0.84);
        const designRowBudget = Math.round(
          Math.max(0, (availH - sectionGap) / 2),
        );
        const designUsableRowH = Math.max(0, designRowBudget - cellInternalGap);

        const gridLandscapeImgMul =
          SYMBOL_SLIDE_4_CONFIG.landscapeImgHeightMul ?? 0.72;
        const designImgMaxH = Math.round(
          designUsableRowH * gridLandscapeImgMul,
        );
        // Same height for both rows — see the portrait branch above.
        const designLpImgMaxH = designImgMaxH;

        const designRow1H =
          designImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * mpMaxLines;
        const designRow2H =
          designLpImgMaxH +
          cellInternalGap +
          designPvFont * pvLineHeight * lpMaxLines;
        const designTotalH = designRow1H + sectionGap + designRow2H;

        const smallLandscapeGridMul = isSmallMobileLandscape
          ? (SYMBOL_SLIDE_4_CONFIG.smallMobileLandscapeScaleMul ?? 1)
          : 1;
        const fillScaleCap = SYMBOL_SLIDE_4_CONFIG.landscapeFillScaleCap ?? 1.3;

        // Width fit — the height fill alone lets symbols balloon on tall/square
        // viewports (e.g. tablet landscape) so the fixed nowrap rows overflow.
        // Cap the scale so each row's images also fit availW, mirroring how the
        // game symbols scale to min(width-fit, height-fit). Driven by the
        // (square) image width only — the pay-value text sits under each symbol
        // and isn't the binding element, so folding it in here would needlessly
        // crush the font.
        const sectionCellGap = Math.round(lp * 1.5); // matches .spt-grid-section gap
        const widthScaleForRow = (cells, designImgW) => {
          const n = cells.length;
          if (n === 0 || designImgW <= 0) return Infinity;
          const avail = Math.max(0, availW - sectionCellGap * (n - 1));
          return avail / (n * designImgW);
        };
        const widthScale = Math.min(
          widthScaleForRow(symbolGridData.midPay, designImgMaxH),
          widthScaleForRow(symbolGridData.lowPay, designLpImgMaxH),
        );

        const scale =
          designTotalH > 0
            ? Math.min(fillScaleCap, availH / designTotalH, widthScale) *
              smallLandscapeGridMul
            : smallLandscapeGridMul;

        const imgMaxH = Math.round(designImgMaxH * scale);
        const lpImgMaxH = Math.round(designLpImgMaxH * scale);
        const pvFontSize = Math.round(designPvFont * scale);

        slide3 = { imgMaxH, lpImgMaxH, pvFontSize };
      }
    }

    // ── A pass whose inputs are not ready yet is not stored (2026-10-28) ──
    // Before the UI scale is known the whole sum is NaN, and the picture boxes
    // were handed `height: NaN` (React's warning). The last good sizes — or
    // the fallbacks — stay until the next pass, which has them.
    if (![slide1, slideHnw].every((s) => Number.isFinite(s.imgH))) return;

    // Only update state if values changed (prevents infinite loop)
    setSlideSizes((prev) => {
      // const next = { slide1, slide2, slide3 };
      const next = { slide1, slideHnw, slide2, slide3 };
      if (prev) {
        const same = JSON.stringify(prev) === JSON.stringify(next);
        if (same) return prev;
      }
      return next;
    });
  }, [
    shouldRender,
    dimensions.width,
    dimensions.height,
    isPortrait,
    descBaseFontSize,
    chrome,
    device,
    layoutDevice,
    isMobileSmall,
    isSmallMobileLandscape,
    isTunedStandardPortrait,
    isTunedMobileLandscape,
    isSmallMobilePortrait,
    landscapeFixedDescFont,
    smallMobileLandscapeDescMul,
    uniformDescFontMul,
    uniformFeatureSubtitleMul,
    uniformDescFontSize,
    t,
    symbolGridData,
    getSlideDescFontMul,
    getUniformFeatureSubtitleH,
    measureTick,
  ]);

  // ── Titles never run into each other — ONE title size on every page ──
  // (Loc QA, 2026-10-28.) The symbol titles are unwrapped, side by side in
  // equal columns, and nothing checked them: a long name (RU "Символ
  // Сундука", NL "Schatkist-symbool") ran into the next one. Every title is
  // measured against its own box (the real font, any language) and ALL of
  // them take the smallest fit — the description rule below, for the same
  // reason. Only ever smaller than the design size, never bigger.
  useLayoutEffect(() => {
    const root = overlayRef.current;
    if (!root) return;
    const titles = [...root.querySelectorAll(".spt-slide .spt-feature-subtitle")];
    titles.forEach((el) => {
      el.style.fontSize = "";
    });
    // let fit = 1;
    // titles.forEach((el) => {
    //   const room = (el.parentElement?.clientWidth || 0) * PAYTABLE_TITLE_FILL;
    //   const needed = el.scrollWidth;
    //   if (room > 0 && needed > room) fit = Math.min(fit, room / needed);
    // });
    // if (fit < 1) {
    //   titles.forEach((el) => {
    //     const size = parseFloat(window.getComputedStyle(el).fontSize) || 0;
    //     if (size > 0) el.style.fontSize = `${Math.floor(size * fit)}px`;
    //   });
    // }
    // ── One size PER PAGE, not across all pages (Loc QA 2026-11-02) ── one
    // long title (zh-CN "…(Cleopatra's Respins)", FI "Kleopatran
    // Uudelleenpyöräytykset") shrank Wild / Scatter on another page. The
    // titles side by side on a page still share one size.
    const pages = new Map();
    titles.forEach((el) => {
      const page = el.closest(".spt-slide-content") || root;
      if (!pages.has(page)) pages.set(page, []);
      pages.get(page).push(el);
    });
    pages.forEach((pageTitles) => {
      let fit = 1;
      pageTitles.forEach((el) => {
        // const room = (el.parentElement?.clientWidth || 0) * PAYTABLE_TITLE_FILL;
        // A flattened section (display: contents) has no box — its parent's.
        const box = el.parentElement?.clientWidth || el.parentElement?.parentElement?.clientWidth || 0;
        const room = box * PAYTABLE_TITLE_FILL;
        const needed = el.scrollWidth;
        if (room > 0 && needed > room) fit = Math.min(fit, room / needed);
      });
      if (fit < 1) {
        pageTitles.forEach((el) => {
          const size = parseFloat(window.getComputedStyle(el).fontSize) || 0;
          if (size > 0) el.style.fontSize = `${Math.floor(size * fit)}px`;
        });
      }
    });
  }, [
    overlayRef,
    shouldRender,
    t,
    slideSizes,
    featureSubtitleFontSize,
    dimensions.width,
    dimensions.height,
    isPortrait,
  ]);

  // ── ONE description size on every page (Art feedback, 2026-10-13: "better
  // to have smaller font sizes over all pages than small text on one page") ──
  // Each page still fits its own text, then every description takes the
  // SMALLEST of those fits — the Hold & Win symbols page (three sections)
  // was the one that came out smaller. The pay values table keeps its own.
  const fittedDescSizes = [
    slideSizes?.slide1?.fontSize,
    slideSizes?.slideHnw?.fontSize,
    ...(featureFontSizes || []).map((f) => f?.fontSize),
  ].filter((n) => n > 0);
  // const sharedDescFontSize = fittedDescSizes.length ? Math.min(...fittedDescSizes) : null;
  // ── Portrait: never below a readable share of the title (Loc QA pass,
  // 2026-10-28: "descriptions are barely readable while titles are bigger …
  // space is not used properly") ── the symbol slides' fits came out ~9 px
  // under ~20 px titles, and the shared minimum handed that to every page,
  // the roomy ones too. Their pictures fill whatever height is left
  // (portrait CSS), so they give way to the text, not the text to them.
  const portraitDescFloor = isPortrait
    ? Math.round(featureSubtitleFontSize * PORTRAIT_DESC_TO_TITLE)
    : 0;
  const sharedDescFontSize = fittedDescSizes.length
    ? Math.max(Math.min(...fittedDescSizes), portraitDescFloor)
    : null;
  const sharedDesc = (own) => sharedDescFontSize ?? own;
  // A width capped in font units keeps its wrap at the shared size.
  const sharedDescWidth = (w, own) =>
    sharedDescFontSize && own > 0 ? Math.round((w * sharedDescFontSize) / own) : w;

  const slides = useMemo(
    () =>
      SLIDE_TITLES.map((title, i) => () => (
        <div
          className={`spt-slide-content${i === 4 || i === 6 ? " spt-slide-content-spaced" : ""}`}
          key={`slide-${i}`}
        >
          <div className="spt-slide-title">{t(title)}</div>
          {/* ── Slide 1: Wild + Scatter ── */}
          {i === 0 &&
            (() => {
              const sizes = slideSizes?.slide1 || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-symbol-sections-coins"
                  ref={slide1SectionsRef}
                  /* style={{ overflow: "hidden" }} — nothing on the symbol slides is clipped (Vladimir, 2026-09-30) */
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_1.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_1.map((s, si) => {
                      // const imgSrc = s.assetName
                      //   ? gameImages[s.assetName]
                      //   : s.img;
                      // Trimmed to the symbol's own pixels — the spine
                      // picture's frame is mostly empty (glow bounds). A
                      // `composite` is a picture joined from several (the
                      // jackpot coins).
                      const imgSrc = s.composite === "jackpotCoins"
                        ? SpineTextureExtractor.getJackpotCoinsCompositeDataURL()
                        : s.symbolName
                          ? SpineTextureExtractor.getTrimmedDataURL(s.symbolName) ||
                            spineDataURLs[s.symbolName]?.src
                          : s.assetName
                            ? gameImages[s.assetName]
                            : s.img;
                      // const vis = s.visualScale || 1;
                      // A per-orientation map too (portrait / landscape — desktop draws as
                      // mobile landscape here), 2026-10-28.
                      const vis =
                        (typeof s.visualScale === "object"
                          ? getImgWidth(s.visualScale, layoutDevice, isPortrait)
                          : s.visualScale) || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          // ? sizes.descWidths[si]
                          ? sharedDescWidth(sizes.descWidths[si], sizes.fontSize)
                          : undefined;
                      return (
                        <div className="spt-symbol-section" key={si}>
                          <div
                            className="spt-symbol-img-wrap"
                            style={{ height: maxImgH }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              // fontSize: `${sizes.fontSize}px`,
                              fontSize: `${sharedDesc(sizes.fontSize)}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 2: Hold & Win symbols — Scarab, Chest, Golden Chest (slide 1's layout) ── */}
          {i === 1 &&
            (() => {
              const sizes = slideSizes?.slideHnw || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  // className="spt-symbol-sections spt-symbol-sections-coins"
                  // + the coins slide's fixed picture share in portrait (Loc QA
                  // 2026-11-02, JA/ZH: the scarab's long text left its picture
                  // tiny, the golden chest's short one made it big).
                  // className="spt-symbol-sections spt-symbol-sections-coins spt-symbol-sections-shared"
                  // ── ...no: a fixed share overflowed on JA (2026-11-02) ──
                  // the text keeps its height and the three pictures split
                  // what is left EQUALLY (spt-symbol-sections-even, portrait).
                  className="spt-symbol-sections spt-symbol-sections-coins spt-symbol-sections-even"
                  ref={hnwSectionsRef}
                  /* style={{ overflow: "hidden" }} — nothing on the symbol slides is clipped (Vladimir, 2026-09-30) */
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_HNW.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_HNW.map((s, si) => {
                      // const imgSrc = s.assetName
                      //   ? gameImages[s.assetName]
                      //   : s.img;
                      // Trimmed to the symbol's own pixels — the spine
                      // picture's frame is mostly empty (glow bounds). A
                      // `composite` is a picture joined from several (the
                      // jackpot coins).
                      const imgSrc = s.composite === "jackpotCoins"
                        ? SpineTextureExtractor.getJackpotCoinsCompositeDataURL()
                        : s.symbolName
                          ? SpineTextureExtractor.getTrimmedDataURL(s.symbolName) ||
                            spineDataURLs[s.symbolName]?.src
                          : s.assetName
                            ? gameImages[s.assetName]
                            : s.img;
                      const vis = s.visualScale || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          // ? sizes.descWidths[si]
                          ? sharedDescWidth(sizes.descWidths[si], sizes.fontSize)
                          : undefined;
                      return (
                        <div className="spt-symbol-section" key={si}>
                          <div
                            className="spt-symbol-img-wrap"
                            // style={{ height: maxImgH }}
                            style={{
                              height: maxImgH,
                              ...(s.portraitImgShare
                                ? { "--spt-img-share": s.portraitImgShare }
                                : {}),
                            }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              // fontSize: `${sizes.fontSize}px`,
                              fontSize: `${sharedDesc(sizes.fontSize)}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 3: Coin + Jackpot Symbols (slide 1's layout and sizes) ── */}
          {i === 2 &&
            (() => {
              const sizes = slideSizes?.slide1 || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-symbol-sections-coins spt-symbol-sections-shared"
                  /* style={{ overflow: "hidden" }} — nothing on the symbol slides is clipped (Vladimir, 2026-09-30) */
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_COINS.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_COINS.map((s, si) => {
                      // const imgSrc = s.assetName
                      //   ? gameImages[s.assetName]
                      //   : s.img;
                      // Trimmed to the symbol's own pixels — the spine
                      // picture's frame is mostly empty (glow bounds). A
                      // `composite` is a picture joined from several (the
                      // jackpot coins).
                      const imgSrc = s.composite === "jackpotCoins"
                        ? SpineTextureExtractor.getJackpotCoinsCompositeDataURL()
                        : s.symbolName
                          ? SpineTextureExtractor.getTrimmedDataURL(s.symbolName) ||
                            spineDataURLs[s.symbolName]?.src
                          : s.assetName
                            ? gameImages[s.assetName]
                            : s.img;
                      const vis = s.visualScale || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          // ? sizes.descWidths[si]
                          ? sharedDescWidth(sizes.descWidths[si], sizes.fontSize)
                          : undefined;
                      return (
                        <div className="spt-symbol-section" key={si}>
                          <div
                            className="spt-symbol-img-wrap"
                            style={{
                              height: maxImgH,
                              ...(s.portraitImgShare
                                ? { "--spt-img-share": s.portraitImgShare }
                                : {}),
                            }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              // fontSize: `${sizes.fontSize}px`,
                              fontSize: `${sharedDesc(sizes.fontSize)}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 6: Unlock more rows + Royal Jackpot (slide 1's layout and sizes) ── */}
          {i === 5 &&
            (() => {
              const sizes = slideSizes?.slide1 || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-symbol-sections-coins"
                  /* style={{ overflow: "hidden" }} — nothing on the symbol slides is clipped (Vladimir, 2026-09-30) */
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_ROWS.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_ROWS.map((s, si) => {
                      // const imgSrc = s.assetName
                      //   ? gameImages[s.assetName]
                      //   : s.img;
                      // Trimmed to the symbol's own pixels — the spine
                      // picture's frame is mostly empty (glow bounds). A
                      // `composite` is a picture joined from several (the
                      // jackpot coins).
                      const imgSrc = s.composite === "jackpotCoins"
                        ? SpineTextureExtractor.getJackpotCoinsCompositeDataURL()
                        : s.symbolName
                          ? SpineTextureExtractor.getTrimmedDataURL(s.symbolName) ||
                            spineDataURLs[s.symbolName]?.src
                          : s.assetName
                            ? gameImages[s.assetName]
                            : s.img;
                      // const vis = s.visualScale || 1;
                      // A per-orientation map too, as the Wild / Scatter slide (2026-10-28).
                      const vis =
                        (typeof s.visualScale === "object"
                          ? getImgWidth(s.visualScale, layoutDevice, isPortrait)
                          : s.visualScale) || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          // ? sizes.descWidths[si]
                          ? sharedDescWidth(sizes.descWidths[si], sizes.fontSize)
                          : undefined;
                      return (
                        <div
                          className="spt-symbol-section"
                          key={si}
                          style={
                            isPortrait && s.portraitSectionGrow
                              ? { flexGrow: s.portraitSectionGrow }
                              : undefined
                          }
                        >
                          <div
                            className="spt-symbol-img-wrap"
                            style={{ height: maxImgH }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                  // Grown from the box's bottom when the item says so — up, away
                                  // from its title (visualOrigin).
                                  transformOrigin: s.visualOrigin,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              // fontSize: `${sizes.fontSize}px`,
                              fontSize: `${sharedDesc(sizes.fontSize)}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 7: Free Spins — slide 1's layout, one item ── NOT USED since
              Free Spins takes the Respins slide's layout (FEATURE_SLIDES[1]) */}
          {false && i === 6 &&
            (() => {
              const sizes = slideSizes?.slide1 || {
                imgH: 100,
                fontSize: Math.round(
                  descBaseFontSize *
                    getSlideDescFontMul(SYMBOL_SLIDE_1_CONFIG.descFontMul),
                ),
              };
              return (
                <div
                  className="spt-symbol-sections spt-symbol-sections-coins spt-symbol-sections-shared"
                  /* style={{ overflow: "hidden" }} — nothing on the symbol slides is clipped (Vladimir, 2026-09-30) */
                >
                  {(() => {
                    const slide1ImgScaleMul =
                      SYMBOL_SLIDE_1_CONFIG.imgScaleMul ?? 1;
                    const maxImgH = Math.max(
                      ...SYMBOL_SLIDE_FS.map((s) => {
                        const sc =
                          ((typeof s.imgScale === "object"
                            ? getImgWidth(s.imgScale, layoutDevice, isPortrait)
                            : s.imgScale) || 1) * slide1ImgScaleMul;
                        return Math.round(sizes.imgH * sc);
                      }),
                    );
                    return SYMBOL_SLIDE_FS.map((s, si) => {
                      // const imgSrc = s.assetName
                      //   ? gameImages[s.assetName]
                      //   : s.img;
                      // Trimmed to the symbol's own pixels — the spine
                      // picture's frame is mostly empty (glow bounds). A
                      // `composite` is a picture joined from several (the
                      // jackpot coins).
                      const imgSrc = s.composite === "jackpotCoins"
                        ? SpineTextureExtractor.getJackpotCoinsCompositeDataURL()
                        : s.symbolName
                          ? SpineTextureExtractor.getTrimmedDataURL(s.symbolName) ||
                            spineDataURLs[s.symbolName]?.src
                          : s.assetName
                            ? gameImages[s.assetName]
                            : s.img;
                      const vis = s.visualScale || 1;
                      const descW =
                        !isTunedMobileLandscape &&
                        !isPortrait &&
                        sizes.descWidths
                          // ? sizes.descWidths[si]
                          ? sharedDescWidth(sizes.descWidths[si], sizes.fontSize)
                          : undefined;
                      return (
                        <div className="spt-symbol-section" key={si}>
                          <div
                            className="spt-symbol-img-wrap"
                            style={{
                              height: maxImgH,
                              ...(s.portraitImgShare
                                ? { "--spt-img-share": s.portraitImgShare }
                                : {}),
                            }}
                          >
                            {imgSrc && (
                              <img
                                className="spt-symbol-img"
                                src={imgSrc}
                                alt={t(s.subtitle)}
                                style={{
                                  height: "100%",
                                  objectFit: "contain",
                                  transform:
                                    vis !== 1 ? `scale(${vis})` : undefined,
                                }}
                              />
                            )}
                          </div>
                          <div className="spt-feature-subtitle">
                            {t(s.subtitle)}
                          </div>
                          <p
                            className="spt-feature-desc"
                            style={{
                              // fontSize: `${sizes.fontSize}px`,
                              fontSize: `${sharedDesc(sizes.fontSize)}px`,
                              width: descW,
                            }}
                          >
                            {t(s.desc)}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })()}
          {/* ── Slide 4: Regular Symbols Grid (mid pay + low pay) ── */}
          {/* {i === 1 && */}
          {/* {i === 2 && */}
          {i === 3 &&
            (() => {
              const sizes = slideSizes?.slide3 || {
                imgMaxH: 60,
                lpImgMaxH: 50,
                pvFontSize: 12,
              };

              const renderGridCell = (sym, symIdx, { isLp = false } = {}) => {
                const imgH = isLp ? sizes.lpImgMaxH : sizes.imgMaxH;

                return (
                  <div className="spt-grid-cell" key={symIdx}>
                    <div className="spt-grid-img-wrap" style={{ height: imgH }}>
                      {sym.imgSrc && (
                        <img
                          className="spt-grid-img"
                          src={sym.imgSrc}
                          alt={sym.name}
                          style={{ maxHeight: imgH, objectFit: "contain" }}
                        />
                      )}
                    </div>
                    <div className="spt-grid-values">
                      {sym.payValues.map((pv, pvIdx) => (
                        <span
                          className="spt-grid-pv"
                          key={pvIdx}
                          style={{
                            fontSize: `${sizes.pvFontSize}px`,
                          }}
                        >
                          {pv.count}: {pv.payout} {currency || ""}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              };

              if (isPortrait) {
                const { midPay, lowPay } = symbolGridData;
                const twoColSymbols = [...midPay, ...lowPay.slice(0, 2)];
                const threeColSymbols = lowPay.slice(2, 5);
                return (
                  <div className="spt-grid-content" ref={gridContentRef}>
                    <div className="spt-grid-block spt-grid-block-2col">
                      {twoColSymbols.map((sym, symIdx) =>
                        renderGridCell(sym, symIdx, { isLp: symIdx >= 4 }),
                      )}
                    </div>
                    <div className="spt-grid-block spt-grid-block-3col">
                      {threeColSymbols.map((sym, symIdx) =>
                        renderGridCell(sym, symIdx, { isLp: true }),
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div className="spt-grid-content" ref={gridContentRef}>
                  {[symbolGridData.midPay, symbolGridData.lowPay].map(
                    (section, si) => {
                      const isLp = si === 1;
                      return (
                        <div
                          className={`spt-grid-section ${isLp ? "spt-grid-lp" : "spt-grid-mp"}`}
                          key={si}
                        >
                          {section.map((sym, symIdx) =>
                            renderGridCell(sym, symIdx, { isLp }),
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              );
            })()}
          {/* ── Slides 3–6: Feature slides ── */}
          {/* i 2..5 before the Hold & Win symbols slide (index 1) */}
          {/* i 3..6 before the coins slide (index 2) */}
          {/* {i >= 4 && i <= 8 && i !== 5 && — Mad Labs' feature slides removed */}
          {/* {i === 4 && */}
          {(i === 4 || i === 6) &&
            (() => {
              // const fi = i - 2;
              // const fi = i - 3;
              // const fi = i - 4;
              // FEATURE_SLIDES[0] at 4, [1..3] at 6..8 (5 is the rows slide).
              // const fi = i === 4 ? 0 : i - 5;
              // const fi = 0; // Cleopatra's Respins — the only feature-style slide left
              // Cleopatra's Respins (4) and Free Spins (6) — one layout.
              const fi = i === 4 ? 0 : 1;
              const f = FEATURE_SLIDES[fi];
              const featureData = (featureFontSizes &&
                featureFontSizes[fi]) || {
                fontSize: Math.round(
                  descBaseFontSize * getSlideDescFontMul(f.descFontMul),
                ),
                descWidths: [],
              };
              const fontSize = featureData.fontSize;
              // descFontMul is baked into the fit effect — use the returned size directly.
              // const descFontSize = fontSize;
              const descFontSize = sharedDesc(fontSize);
              const portraitImgScale = isPortrait
                ? 0.8 * (f.portraitImgMul ?? 1)
                : 1;
              const landscapeImgScale =
                isTunedMobileLandscape && f.landscapeImgMul != null
                  ? f.landscapeImgMul
                  : 1;
              const imgScale = isPortrait
                ? portraitImgScale
                : landscapeImgScale;
              // ── Which layout: the column, or image-left / text-right ──
              //
              // Portrait has always been the column. `stackedAlways` opts a
              // slide into it on landscape and desktop as well — Collect,
              // Double and Mystery, per the client reference. Gamble keeps the
              // row.
              const stacked = isPortrait || f.stackedAlways === true;
              const isLandscapeStacked = stacked && !isPortrait;

              // ── The image keeps the SIZE it has today ──
              //
              // `imgWidth` is a percentage OF ITS WRAPPER, and the wrapper is
              // 50% of the slide in the row layout but 100% in the column. Left
              // alone, moving a slide to the column would silently draw its
              // image at double the width — a size change, when this is meant
              // to be a reorder. Halving the percentage cancels the wrapper
              // exactly, so nothing on screen resizes.
              //
              // Drop this factor when the sizes are tuned for the new layout;
              // the config percentages then mean what they say again.
              const stackedWidthFactor = isLandscapeStacked ? 0.5 : 1;
              const imgWidth = `calc(${getImgWidth(f.imgWidth, layoutDevice, isPortrait)} * ${imgScale} * ${stackedWidthFactor})`;
              const descMarginTop = f.descMarginTop
                ? getImgWidth(f.descMarginTop, layoutDevice, isPortrait)
                : undefined;
              const imgMarginTop = f.imgMarginTop
                ? getImgWidth(f.imgMarginTop, layoutDevice, isPortrait)
                : undefined;
              const stackedBodyMarginTop = f.stackedBodyMarginTop
                ? getImgWidth(f.stackedBodyMarginTop, layoutDevice, isPortrait)
                : undefined;
              const featureDesc = t(f.desc);
              const descStyle = {
                fontSize: `${descFontSize}px`,
                ...(descMarginTop ? { marginTop: descMarginTop } : {}),
              };
              // ── Fill the band, instead of being sized by width ──
              //
              // Normally the picture's WIDTH is what decides how big it is, and
              // the band merely caps its height — so a slide whose art is wide
              // and short leaves most of its band empty. `imgFillsBand` hands
              // the whole wrapper to the image and lets `object-fit: contain`
              // pick the largest size that fits, which is the biggest it can be
              // without changing the band or the line the description sits on.
              //
              // Portrait only for now: the landscape column is already tuned
              // through STACKED_LANDSCAPE_IMG_MAX_H and its own width factor.
              //    A PERCENT of the wrapper, so it can be dialled back off the
              //    edges; `true` means 100. The picture is centred in whatever
              //    box this makes, so 90 shrinks it evenly rather than
              //    cropping or shifting it.
              const imgFillPct =
                isPortrait && f.imgFillsBand
                  ? f.imgFillsBand === true
                    ? 100
                    : f.imgFillsBand
                  : null;
              // const featureImgStyle =
              //   imgFillPct != null
              //     ? { width: `${imgFillPct}%`, height: `${imgFillPct}%` }
              //     : { width: imgWidth };
              // ── Past 100: the picture's GLOW may leave the band ──
              // (2026-10-28, Respins: "nice ratio … all visible all nice", on
              // every phone.) Sized by the band, not a fixed scale, so its
              // board takes the same share of the band on a small phone as
              // on a big one; the glow runs past it, behind the title and
              // the description (raised above it below), never cut off.
              const imgPastBand = imgFillPct != null && imgFillPct > 100;
              const featureImgStyle = {
                ...(imgFillPct != null
                  ? { width: `${imgFillPct}%`, height: `${imgFillPct}%` }
                  : { width: imgWidth }),
                ...(imgPastBand ? { maxHeight: "none", flexShrink: 0 } : {}),
              };
              const overBandText = imgPastBand
                ? { position: "relative", zIndex: 1 }
                : undefined;

              const imgWrapStyle = imgMarginTop
                ? { marginTop: imgMarginTop }
                : undefined;
              // Portrait-only on purpose: these were tuned against the portrait
              // column and are not a statement about the landscape one.
              const stackedBodyStyle =
                isPortrait && stackedBodyMarginTop
                  ? { marginTop: stackedBodyMarginTop }
                  : undefined;
              // ── Per-slide gaps in the PORTRAIT column ──
              //
              // The stacked gaps are one shared `linePad` in the CSS, so a
              // slide that wants more air cannot ask for it there without
              // moving every other slide with it. This is the landscape
              // `landscapeBodyGapMul`'s opposite number, applied to BOTH the
              // title-to-body gap and the image-to-text gap so the column stays
              // evenly spaced rather than growing at one seam.
              const portraitStackedGap =
                isPortrait && f.portraitBodyGapMul != null
                  ? { gap: Math.round(linePad * f.portraitBodyGapMul) }
                  : undefined;

              const featureBodyStyle =
                !isPortrait && f.landscapeBodyGapMul != null
                  ? { gap: Math.round(linePad * f.landscapeBodyGapMul) }
                  : undefined;

              // Feature image composited from two separate images (pot + coins)
              // stacked directly, plus coinsGapMul as a per-slide fine-tune —
              // avoids shipping a third, redundant combined image asset.
              const splitImages = f.potAssetName ? (
                <div className="spt-feature-split-stack">
                  <img
                    className="spt-feature-img spt-feature-split-img"
                    src={gameImages[f.potAssetName]}
                    alt={t(f.subtitle)}
                    style={{ width: imgWidth }}
                  />
                  <img
                    className="spt-feature-img spt-feature-split-img"
                    src={gameImages[f.coinsAssetName]}
                    alt=""
                    style={{
                      width: imgWidth,
                      marginTop: `calc(${imgWidth} * ${f.coinsGapMul ?? 0})`,
                    }}
                  />
                </div>
              ) : null;

              if (stacked) {
                return (
                  <div
                    className={`spt-feature-content spt-feature-stacked${
                      isLandscapeStacked ? " spt-feature-stacked-landscape" : ""
                    }`}
                  >
                    <div
                      className="spt-feature-stacked-inner"
                      style={{
                        // ── Titles line up across every slide ──
                        //
                        // In the row layout the subtitle sits inside
                        // `.spt-feature-text-wrap`, which carries
                        // featureTextWrapPadTop. The column has no such wrapper,
                        // so without this the three converted slides would show
                        // their title higher than the Gamble slide beside them.
                        //
                        // The same rule the CSS uses for
                        // `.spt-feature-text-wrap`'s padding-top, recomputed
                        // here because that constant lives in the style block's
                        // scope. If one moves, move both.
                        ...(isLandscapeStacked
                          ? {
                              paddingTop: isTunedMobileLandscape
                                ? 0
                                : Math.round(linePad * 1.5),
                            }
                          : {}),
                        ...portraitStackedGap,
                        ...(splitImages ? { overflow: "visible" } : {}),
                        ...(imgPastBand ? { overflow: "visible" } : {}),
                      }}
                      ref={(el) => {
                        featureBodyRefs.current[fi] = el;
                      }}
                    >
                      <div className="spt-feature-subtitle" style={overBandText}>
                        {t(f.subtitle)}
                      </div>
                      <div
                        className="spt-feature-stacked-body"
                        style={{
                          ...stackedBodyStyle,
                          ...portraitStackedGap,
                          ...(splitImages ? { overflow: "visible" } : {}),
                          ...(imgPastBand ? { overflow: "visible" } : {}),
                        }}
                      >
                        <div
                          className="spt-feature-img-wrap"
                          style={{
                            ...imgWrapStyle,
                            ...(splitImages ? { overflow: "visible" } : {}),
                            // The bigger picture's glow is not cut at the band.
                            ...(imgPastBand ? { overflow: "visible" } : {}),
                          }}
                        >
                          {splitImages || (
                            <img
                              className="spt-feature-img"
                              src={
                                f.symbolName ? SpineTextureExtractor.getTrimmedDataURL(f.symbolName) || spineDataURLs[f.symbolName]?.src : f.assetName ? gameImages[f.assetName] : f.img
                              }
                              alt={t(f.subtitle)}
                              style={featureImgStyle}
                            />
                          )}
                        </div>
                        <p
                          className="spt-feature-desc"
                          // style={descStyle}
                          style={{ ...descStyle, ...overBandText }}
                        >
                          {featureDesc}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div className="spt-feature-content">
                  <div
                    className="spt-feature-body"
                    style={{
                      ...featureBodyStyle,
                      ...(splitImages ? { overflow: "visible" } : {}),
                      // A picture lifted above the body (imgMarginTop < 0)
                      // keeps its glow — not cut flat at the body's top.
                      ...(f.imgOverflowVisible ? { overflow: "visible" } : {}),
                    }}
                    ref={(el) => {
                      featureBodyRefs.current[fi] = el;
                    }}
                  >
                    <div
                      className="spt-feature-img-wrap"
                      style={{
                        ...imgWrapStyle,
                        ...(splitImages ? { overflow: "visible" } : {}),
                        ...(f.imgOverflowVisible ? { overflow: "visible" } : {}),
                      }}
                    >
                      {splitImages || (
                        <img
                          className="spt-feature-img"
                          src={f.symbolName ? SpineTextureExtractor.getTrimmedDataURL(f.symbolName) || spineDataURLs[f.symbolName]?.src : f.assetName ? gameImages[f.assetName] : f.img}
                          alt={t(f.subtitle)}
                          // style={{ width: imgWidth }}
                          // A picture let out of its box (imgOverflowVisible) is sized by its
                          // width alone — the box's height no longer caps it.
                          style={{
                            width: imgWidth,
                            ...(f.imgOverflowVisible
                              ? { maxHeight: "none", flexShrink: 0 }
                              : {}),
                          }}
                        />
                      )}
                    </div>
                    <div
                      className="spt-feature-text-wrap"
                      style={{
                        ...(!isTunedMobileLandscape && f.textPadMul != null
                          ? {
                              paddingTop: Math.round(
                                Math.round(chrome.title * 0.5) * f.textPadMul,
                              ),
                            }
                          : {}),
                        ...(f.subtitleNoWrap ? { overflow: "visible" } : {}),
                      }}
                    >
                      <div
                        className="spt-feature-subtitle"
                        style={
                          f.subtitleNoWrap
                            ? { whiteSpace: "nowrap" }
                            : undefined
                        }
                      >
                        {t(f.subtitle)}
                      </div>
                      <p className="spt-feature-desc" style={descStyle}>
                        {featureDesc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          {/* ── Slide 7: Paylines ── */}
          {/* i === 9 before Mad Labs' feature slides were removed */}
          {i === 7 && (
            <img
              className="spt-paylines-img"
              src={paylineImgSrc}
              alt="Paylines"
            />
          )}
        </div>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      paylineImgSrc,
      device,
      layoutDevice,
      isPortrait,
      featureFontSizes,
      descBaseFontSize,
      slideSizes,
      symbolGridData,
      currency,
      sharedDescFontSize,
    ],
  );

  // ── Memoized overlay stylesheet — shell + slide content styles ──
  const overlayStyles = useMemo(() => {
    const zeusContentGap = isTunedMobileLandscape
      ? linePad
      : Math.round(linePad * (SYMBOL_SLIDE_2_CONFIG.contentGapMul ?? 0.8));
    const symbolSectionsGap = isTunedMobileLandscape
      ? linePad
      : isPortrait
        ? Math.round(linePad * 1.6)
        : Math.round(linePad * 1.5);
    const symbolSectionFlex = isTunedMobileLandscape
      ? "1 1 0"
      : isPortrait
        ? "0 0 auto"
        : "1";
    const symbolSectionsCoinsJustify = isTunedMobileLandscape
      ? "center"
      : "flex-start";
    const zeusSectionRowGap = Math.round(
      linePad * (SYMBOL_SLIDE_2_CONFIG.sectionRowGapMul ?? 0.8),
    );
    const zeusSectionInnerGap = Math.round(
      linePad * (SYMBOL_SLIDE_2_CONFIG.sectionInnerGapMul ?? 0.8),
    );
    // Landscape uses the fixed uniform-scale design (see SYMBOL_SLIDE_2 fit).
    const zeusDesign = SYMBOL_SLIDE_2_CONFIG.landscapeDesign;
    const featureImgWrapPadRight = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.8);
    // ── How much of the landscape column the IMAGE may take ──
    //
    // Percent of the space left after the title and the description. The image
    // wrapper is a flex child that would otherwise absorb ALL of it, and with
    // "object-fit: contain" the picture then sits in a box far taller than
    // itself — space that reads as empty because nothing is drawn in it.
    //
    // The image FILLS this wrapper (height 100%), so this number alone decides
    // how much room the picture gets — lower it and the image shrinks with it,
    // the slack collecting under the description. 85 -> 72 -> 61.
    //
    // Landscape only — portrait sizes the image from its own height and is not
    // affected.
    const STACKED_LANDSCAPE_IMG_MAX_H = 61;

    // ── The PORTRAIT image band, shared by every feature slide ──
    //
    // Percent of the body the image wrapper occupies, FIXED — not sized to the
    // picture inside it. That is the whole point: designers want the image and
    // the description on the same line across all four slides, and the images
    // are different shapes, so a wrapper that hugs its content puts the
    // description somewhere different on every slide. A band of declared height
    // puts it in the same place by construction.
    //
    // The picture is centred inside the band and capped to it, so a small image
    // stays small — matching the reference, where the four images differ in
    // size but share a line.
    //
    // Deliberately NOT measured from the gamble slide at runtime: that would
    // make three slides depend on a fourth being mounted and laid out first,
    // inside a slider that need not have rendered it, and would have to be
    // re-run on every resize. A constant is right on the first paint and cannot
    // get out of order.
    const STACKED_PORTRAIT_IMG_BAND = 52;

    // ── Slide 0's internal gap, matched to the feature slides ──
    //
    // The wild/scatter sections ran at 0.4 x linePad while the feature column
    // runs at 1.5 — nearly four times tighter, on screens the player flicks
    // between. This lines the two up in PORTRAIT so the whole paytable breathes
    // alike; landscape keeps 0.4, where slide 0 puts its two sections side by
    // side and has far less height to give away.
    //
    // // Keep this in step with `portraitBodyGapMul` on the feature slides.
    // const symbolSectionGapMul = isPortrait ? 1.5 : 0.4;
    // Portrait was 1.5, in step with `portraitBodyGapMul` on the feature
    // slides; now halved for this slide only — see the constant.
    const symbolSectionGapMul = isPortrait
      ? SYMBOL_SECTION_GAP_MUL_PORTRAIT
      : 0.4;

    // NOTE: the landscape-stacked branch in the render recomputes this exact
    // rule, so a slide in the column keeps its title on the same line as one in
    // the row. Change one and change the other.
    const featureTextWrapPadTop = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 1.5);
    const featureTextWrapPadLeft = isTunedMobileLandscape
      ? 0
      : Math.round(linePad * 0.5);
    const featureTextWrapGap = isTunedMobileLandscape
      ? Math.round(linePad * 0.4)
      : Math.round(linePad * 0.5);
    const featureImgWrapJustify = isTunedMobileLandscape
      ? "center"
      : "flex-end";
    const featureBodyOverflow = isTunedMobileLandscape ? "visible" : "hidden";
    const featureImgWrapOverflow = isTunedMobileLandscape
      ? "visible"
      : "hidden";
    const symbolDescPadH = isTunedMobileLandscape
      ? 0
      : Math.round(chrome.base * 0.3);
    // One gap in both directions, and between both blocks: the 2-col grid used
    // 0.3 between rows against 0.7 between columns, so the same slide had two
    // different rhythms.
    const gridPortraitRowGap = Math.round(linePad * 0.7);
    // ── The two COLUMNS get double the row gap, on phones only ──
    //
    // Rows were fine; the two columns sat too close together. Doubling the
    // horizontal gap alone spreads them without touching the vertical rhythm —
    // and the vertical is what the height fit is budgeted against, so leaving
    // it alone keeps the grid inside the slide.
    //
    // STANDARD AND SMALL MOBILE PORTRAIT ONLY, deliberately not `isPortrait`:
    // that would take tablet portrait with it, where the grid has plenty of
    // width already and the extra gap only flings the columns to the edges.
    const isPhonePortrait = isStandardMobilePortrait || isSmallMobilePortrait;
    const gridPortraitColGap = Math.round(
      linePad * (isPhonePortrait ? 1.4 : 0.7),
    );
    const gridContentGap = isPortrait ? gridPortraitRowGap : linePad;
    const gridCellGap = Math.round(linePad * 0.1);
    return `
    .spt-overlay { font-family: 'Noto Sans', sans-serif; }

    /* ── HEADER ── */
    .spt-header {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-bottom: ${headerPaddingBottom}px;
      position: relative;
      z-index: 1;
    }
    .spt-header-title-row {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-title {
      flex: 1;
      min-width: 0;
      box-sizing: border-box;
      padding: 0 ${titleSideReserve}px;
      /* Flex-centred, so an overflow (before the fit lands) spills evenly
         and the scale below shrinks it around the row's own centre. */
      display: flex;
      justify-content: center;
      font-size: ${headerTitleFontSize}px; font-weight: 700;
      color: #ffffff; margin: 0; text-align: center; white-space: nowrap;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    }
    .spt-title-text {
      display: inline-block;
      flex-shrink: 0;
      transform-origin: center center;
    }
    .spt-separator {
      ${
        isTunedShellPortrait
          ? `position: relative;
      margin-top: ${separatorMarginTop}px;
      margin-bottom: ${separatorMarginBottom}px;`
          : `position: absolute;
      bottom: 0; left: 50%; transform: translateX(-50%);`
      }
      width: ${separatorLineWidth}px;
      height: ${separatorHeight}px;
      background: linear-gradient(to right, transparent, #ffc102 35%, #ffc102 65%, transparent);
      clip-path: ellipse(50% 50% at 50% 50%);
    }
    .spt-close-btn {
      position: absolute;
      top: 50%; transform: translateY(-50%);
      right: ${closeButtonRight}px;
      width: ${closeButtonSize}px; height: ${closeButtonSize}px;
      background-color: transparent; border: none; cursor: pointer; padding: 0;
      background-size: contain; background-repeat: no-repeat;
      background-position: center; transition: filter 0.2s ease;
      z-index: 1010;
    }
    .spt-close-btn:hover { filter: brightness(2.0); }

    /* Decorative glow — standard mobile portrait */
    .spt-glow {
      position: absolute;
      top: ${glowTop};
      left: 50%;
      transform: translateX(-50%);
      width: ${glowWidth};
      pointer-events: none;
      z-index: 0;
      opacity: 1;
    }
    /* ── SLIDE CONTENT — base layout for all slide types ── */
    .spt-slide-content {
      flex: 1; min-height: 0;
      width: 100%;
      max-width: ${
        (isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait) &&
        shellContentWidth != null
          ? `${shellContentWidth}px`
          : isPortrait
            ? `calc(100% - ${arrowSafeZone}px)`
            : "100%"
      };
      min-width: 0;
      margin: 0;
      box-sizing: border-box;
      display: flex; flex-direction: column;
      align-items: center; justify-content: flex-start;
      gap: ${slideContentGap}px;
      position: relative;
      z-index: 1;
    }
    .spt-slide-content-spaced {
      gap: ${slideContentSpacedGap}px;
    }
    .spt-slide-title {
      font-size: ${slideTitleFontSize}px; font-weight: bold;
      color: #ffc102; text-align: center;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      margin-bottom: 0;
    }

    /* ── Slide content: Paylines ── */
    .spt-paylines-img {
      flex: 1;
      min-height: 0;
      width: 100%;
      object-fit: contain;
    }

    /* ── Slide content: Features ── */
    .spt-feature-content {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
    }
    .spt-feature-subtitle {
      font-size: ${featureSubtitleFontSize}px;
      font-weight: 700;
      color: #ffffff;
      text-align: center;
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
      margin: 0;
      flex-shrink: 0;
    }
    .spt-feature-body {
      flex: 1;
      min-height: 0;
      overflow: ${featureBodyOverflow};
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: flex-start;
    }
    .spt-feature-img-wrap {
      position: relative;
      width: 50%;
      height: 100%;
      min-height: 0;
      display: flex;
      align-items: flex-start;
      justify-content: ${featureImgWrapJustify};
      overflow: ${featureImgWrapOverflow};
      padding-right: ${featureImgWrapPadRight}px;
      box-sizing: border-box;
    }
    /* Feature image composited from two split images (pot + coins) — see splitImages in render */
    .spt-feature-split-stack {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      height: 100%;
      min-height: 0;
    }
    .spt-feature-split-img {
      display: block;
    }
    .spt-feature-text-wrap {
      width: 50%;
      height: 100%;
      padding-top: ${featureTextWrapPadTop}px;
      padding-left: ${featureTextWrapPadLeft}px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${featureTextWrapGap}px;
      overflow: hidden;
    }
    .spt-feature-img {
      object-fit: contain;
      max-height: 100%;
      /* iOS: pin to own pixel-aligned layer so the scaled WebP's bottom edge
         doesn't render a faint 1px seam line against the dark backdrop */
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
      transform: translateZ(0);
    }
    .spt-feature-desc {
      /* font-size set inline via descFontSizes */
      font-weight: 500;
      -webkit-text-stroke: 0.3px rgba(255,255,255,0.5);
      color: #ffffff;
      text-align: center;
      line-height: 1.4;
      white-space: pre-line;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      margin: 0;
      overflow-wrap: break-word;
    }
    .spt-feature-stacked {
      justify-content: flex-start;
    }
    .spt-feature-stacked-inner {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked-body {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: ${linePad}px;
      overflow: hidden;
    }
    .spt-feature-stacked .spt-feature-img-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      /* A BAND of declared height, not a wrapper that hugs its picture — see
         STACKED_PORTRAIT_IMG_BAND. This is what puts the description on the
         same line on every feature slide. */
      flex: 0 0 ${STACKED_PORTRAIT_IMG_BAND}%;
      width: 100%;
      padding-right: 0;
    }
    .spt-feature-stacked .spt-feature-img {
      object-fit: contain;
      /* Capped to the band and centred in it, so a smaller picture stays
         smaller instead of stretching to fill the slot. */
      max-height: 100%;
    }
    /* ── The column in LANDSCAPE has to fit, not overflow ──
       Portrait can let the image run at its natural height: the column is tall
       and "max-height: none" with "flex-shrink: 0" is what keeps it crisp.
       Landscape is short, so the same rules push the image past the body and
       "overflow: hidden" cuts it — which is exactly what was happening.
       Here the wrapper is allowed to SHRINK and the image is capped to it;
       "object-fit: contain" then letterboxes rather than crops, so the whole
       picture is always visible whatever height is left over.
       NOTE: no backticks in this block — it is inside a template literal. */
    .spt-feature-stacked-landscape .spt-feature-img-wrap {
      flex: 1 1 auto;
      min-height: 0;
      max-height: ${STACKED_LANDSCAPE_IMG_MAX_H}%;
    }
    .spt-feature-stacked-landscape .spt-feature-img {
      /* FILL the wrapper vertically rather than merely being capped by it.
         With "max-height" alone the picture stops at whatever its width allows
         and leaves slack inside the wrapper — so the wrapper reads as the empty
         space, not the image. Height 100% makes the box match the wrapper and
         "object-fit: contain" keeps the aspect, letterboxing instead of
         stretching. The WRAPPER is now the only thing that decides how much
         room the image gets. */
      height: 100%;
      max-height: 100%;
    }
    /* ── No gaps in the landscape column ──
       The image element now takes the whole box the flex row leaves it, and
       "object-fit: contain" centres the picture inside that box — so the
       element is already taller than what is drawn, and that letterboxing IS
       the breathing room. Adding linePad above and below it on top of that is
       what left the slide looking half empty.
       Portrait keeps its gaps: there the image is sized to its own height, so
       nothing separates it from the title and the text but these. */
    .spt-feature-stacked-landscape .spt-feature-stacked-inner,
    .spt-feature-stacked-landscape .spt-feature-stacked-body {
      gap: 0;
    }
    /* The row layout capped the description for free — its text wrapper is 50%
       of the slide. The column has no wrapper, so it needs saying. Kept in step
       with the fit measurement above; see the constant's note. */
    .spt-feature-stacked-landscape .spt-feature-desc {
      max-width: ${STACKED_LANDSCAPE_DESC_MAX_W_PCT}%;
    }


    /* ── Slide content: Symbol sections ── */
    .spt-symbol-sections {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: ${isPortrait ? "column" : "row"};
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
      gap: ${symbolSectionsGap}px;
      overflow: hidden;
    }
    .spt-symbol-sections-coins {
      justify-content: ${symbolSectionsCoinsJustify};
    }
    /* Slides 1 and 2 (Wild + Scatter, the Hold & Win symbols): HALF the gap
       between picture, title and text (Vladimir, 2026-09-30: "this gap can be
       half of what it is now"). */
    .spt-symbol-sections-coins .spt-symbol-section {
      gap: ${Math.round((linePad * symbolSectionGapMul) / 2)}px;
    }
    /* No clipping on the symbol slides — a picture or a line of text is never
       cut off (Vladimir, 2026-09-30: "nor text nor images must not have this
       cutout of overflow hidden"). */
    .spt-symbol-sections-coins,
    .spt-symbol-sections-coins .spt-symbol-section {
      overflow: visible;
    }
    /* PORTRAIT ONLY — each section an equal share of the slide, and the
       picture takes ALL the height its section has left after the title and
       the text (the measured height left the symbols at a fraction of it;
       Vladimir, 2026-09-30). Not in landscape: the sections sit side by side
       at their content's height there, so a picture told to fill "the rest"
       collapsed to nothing (the landscape bug, 2026-09-30) — landscape keeps
       the measured height. Plus half the gap BETWEEN the sections. */
    ${isPortrait ? `
    .spt-symbol-sections-coins {
      gap: ${Math.round(symbolSectionsGap / 2)}px;
    }
    .spt-symbol-sections-coins .spt-symbol-section {
      flex: 1 1 0;
    }
    .spt-symbol-sections-coins .spt-symbol-img-wrap {
      flex: 1 1 0;
      min-height: 0;
      height: auto !important;
    }
    /* The coins slide: each picture a FIXED share of the whole slide body
       (portraitImgShare × its height, container query units), and each
       section only as tall as its content — the two read alike and both fit,
       whatever the text length. */
    .spt-symbol-sections-shared {
      container-type: size;
      justify-content: space-evenly;
    }
    .spt-symbol-sections-shared .spt-symbol-section {
      flex: 0 0 auto;
    }
    .spt-symbol-sections-shared .spt-symbol-img-wrap {
      flex: 0 0 auto;
      height: calc(var(--spt-img-share, 0.22) * 100cqh) !important;
    }
    /* The Hold & Win symbols slide: the sections flattened, so the three
       pictures share what the titles and texts leave — EQUALLY, never past
       the slide (Loc QA 2026-11-02: JA's long texts overflowed a fixed share,
       and per-section leftovers made the scarab tiny). Capped by
       portraitImgShare so a short language does not blow them up. */
    .spt-symbol-sections-even {
      container-type: size;
      /* gap: ${Math.round((linePad * symbolSectionGapMul) / 2)}px; */
      /* (A container's own cqh reads its PARENT, so the spacing is on the
         children below, where cqh is this slide.) */
      gap: 0;
    }
    .spt-symbol-sections-even .spt-feature-subtitle,
    .spt-symbol-sections-even .spt-feature-desc {
      margin: ${HNW_PORTRAIT_GAP_CQH}cqh 0 0;
    }
    .spt-symbol-sections-even .spt-symbol-section {
      display: contents;
    }
    .spt-symbol-sections-even .spt-symbol-img-wrap {
      flex: 1 1 0;
      min-height: 0;
      height: auto !important;
      max-height: calc(var(--spt-img-share, 0.2) * 100cqh);
    }
    .spt-symbol-sections-even .spt-symbol-section + .spt-symbol-section .spt-symbol-img-wrap {
      /* margin-top: ${Math.round(symbolSectionsGap / 2)}px; */
      margin-top: ${HNW_PORTRAIT_GAP_CQH * HNW_PORTRAIT_SECTION_GAP_MUL}cqh;
    }
    ${isSmallMobilePortrait ? "" : `
    .spt-symbol-sections-even .spt-feature-subtitle {
      font-size: ${Math.round(featureSubtitleFontSize * HNW_PORTRAIT_TITLE_MUL)}px;
    }`}` : ""}
    /* SMALL mobile portrait: every symbol and feature picture a bit smaller
       (2026-10-28: "they are too large for such screen" — standard portrait
       stays as approved). The BOXES shrink, not just the drawing ("wrapper
       need to follow image"): the pictures fill their boxes, so they follow,
       and the slide closes up around them. */
    ${isSmallMobilePortrait ? `
    .spt-symbol-sections-coins .spt-symbol-img-wrap {
      flex-grow: ${SMALL_PORTRAIT_IMG_SCALE};
    }
    .spt-symbol-sections-shared .spt-symbol-img-wrap {
      height: calc(var(--spt-img-share, 0.22) * 100cqh * ${SMALL_PORTRAIT_IMG_SCALE}) !important;
    }
    .spt-feature-stacked .spt-feature-img-wrap {
      flex-basis: ${STACKED_PORTRAIT_IMG_BAND * SMALL_PORTRAIT_IMG_SCALE}%;
    }
    /* The paylines picture ran the full width, into the arrows. */
    .spt-paylines-img {
      width: ${SMALL_PORTRAIT_IMG_SCALE * 100}%;
      align-self: center;
    }` : ""}
    .spt-symbol-section {
      flex: ${symbolSectionFlex};
      min-height: 0;
      ${isPortrait ? "width: 100%;" : "min-width: 0;"}
      ${isTunedMobileLandscape && !isPortrait ? "width: 0;" : ""}
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: ${isPortrait ? "center" : "flex-start"};
      text-align: center;
      gap: ${Math.round(linePad * symbolSectionGapMul)}px;
      overflow: hidden;
    }
    .spt-symbol-img-wrap {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      /* height set inline by symbolSlide1Sizes */
    }
    .spt-symbol-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-symbol-section .spt-feature-desc {
      padding: 0 ${symbolDescPadH}px;
    }
    .spt-symbol-section .spt-feature-subtitle {
      /* white-space: nowrap; */
      /* The translators' own line breaks kept (zh-CN "圣甲虫\n(Scarab)",
         2026-10-28), no wrapping of their own — the title fit sizes them. */
      white-space: pre;
    }

    /* ── Slide 2: Zeus Symbols and POTs ── */
    .spt-zeus-slide-wrap {
      flex: 1;
      min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      overflow: hidden;
      ${isPortrait ? "" : `gap: ${featureTextWrapGap}px;`}
    }
    .spt-zeus-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      box-sizing: border-box;
      ${
        isPortrait
          ? `
      width: 100%;
      gap: ${zeusContentGap}px;
      flex: 1;
      min-height: 0;
      justify-content: flex-start;
      `
          : `
      gap: ${zeusDesign.contentGap}px;
      flex: none;
      transform-origin: top center;
      `
      }
    }
    .spt-zeus-sections {
      width: 100%;
      ${
        isPortrait
          ? `
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: ${zeusSectionRowGap}px;
      `
          : `
      display: grid;
      grid-template-columns: 1fr 1fr;
      row-gap: ${zeusDesign.sectionRowGap}px;
      column-gap: ${zeusDesign.sectionInnerGap}px;
      justify-content: center;
      `
      }
      align-items: center;
      justify-items: center;
    }
    .spt-zeus-section {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      justify-content: center;
      gap: ${isPortrait ? zeusSectionInnerGap : zeusDesign.sectionInnerGap}px;
      ${isPortrait ? "width: 100%; box-sizing: border-box;" : "width: auto; max-width: 100%; justify-self: center;"}
    }
    .spt-zeus-section:last-child {
      ${isPortrait ? "" : "grid-column: 1 / -1; max-width: 52%; justify-self: center;"}
    }
    .spt-zeus-img-wrap {
      flex: 0 0 auto;
      width: auto;
      align-self: stretch;
      max-width: ${Math.round(getImgWidth(SYMBOL_SLIDE_2_IMG_RATIO, layoutDevice, isPortrait) * 100)}%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-zeus-img {
      ${isPortrait ? "height: 95%; max-height: 95%;" : `height: auto; max-height: ${zeusDesign.imgH}px;`}
      max-width: 95%;
      object-fit: contain;
    }
    .spt-zeus-desc {
      flex: 1;
      min-width: 0;
      text-align: center;
    }
    .spt-zeus-content .spt-feature-desc {
      white-space: normal;
    }

    /* ── Slide 3: Regular Symbols Grid ── */
    .spt-grid-content {
      flex: 1; min-height: 0;
      width: 100%;
      display: flex;
      flex-direction: column;
      /* WIDTH STAYS 100%. The sizing pass measures this element's clientWidth
         to work out how large the symbols may be, so a content-driven width
         here is a shrink loop: narrower box, smaller symbols, narrower box.
         The rows below control their own spread instead. */
      align-items: stretch;
      justify-content: center;
      gap: ${gridContentGap}px;
    }
    .spt-grid-block {
      justify-content: center;
      width: 100%;
    }
    .spt-grid-block-2col {
      display: grid;
      grid-template-columns: auto auto;
      justify-items: center;
      row-gap: ${gridPortraitRowGap}px;
      column-gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col {
      display: flex;
      flex-wrap: nowrap;
      gap: ${gridPortraitColGap}px;
    }
    .spt-grid-block-3col .spt-grid-cell {
      flex: 0 0 auto;
    }
    .spt-grid-section {
      /* Landscape-only element. A GRID, not a flex row, and that is the fix:
         1fr columns are all the same width, so the two rows line up.

         Flex could not do it. Cells sized to their own content, and the low-pay
         cards are narrower than the professor and the gadgets, so the bottom row
         drew narrower. flex:1 fixed that by stretching them across the full
         width, which pushed them too far apart.

         The WIDTH comes from the parent (see spt-grid-content): both rows are
         handed the same one, so their columns land on top of each other and the
         block is only as wide as its widest row. Never wraps, so the 2-row
         layout is fixed and symbols only scale via the height fit. */
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      /* Both rows fill the same parent width, so their 1fr columns land on top
         of each other. GRID_ROW_MAX_WIDTH pulls them in from the full span so
         they are not flung to the edges — it is the knob for how tight the two
         rows sit, and it is safe to change because nothing measures a row. */
      width: 100%;
      max-width: ${GRID_ROW_MAX_WIDTH};
      margin: 0 auto;
      align-items: center;
      justify-items: center;
      justify-content: center;
      gap: ${Math.round(linePad * 1.5)}px;
    }
    .spt-grid-section.spt-grid-mp {
      justify-content: center;
      align-items: ${isPortrait ? "center" : "flex-start"};
    }
    .spt-grid-section.spt-grid-lp {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: ${isPortrait ? "center" : "flex-start"};
      justify-content: center;
    }
    .spt-grid-cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: ${gridCellGap}px;
    }

    .spt-grid-img-wrap {
      /* height set inline via symbolSlide3Sizes.imgMaxH */
      flex-shrink: 0;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .spt-grid-img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }
    .spt-grid-values {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0;
      flex-shrink: 0;
    }
    .spt-grid-pv {
      /* ── The SAME face as a slide description, only smaller ──
         Weight, colour, stroke and shadow are copied from .spt-feature-desc so
         the paytable reads as one typeface throughout. Only the SIZE differs,
         and it is set inline from symbolSlide3Sizes.pvFontSize because this
         grid fits itself to the space it has.

         These used to be weight 600 at 80% white against the descriptions'
         500 at full white with a hairline stroke — heavier AND dimmer, which
         is why no single weight ever matched them.

         line-height stays 1.3, NOT the description's 1.4: the portrait height
         budget (pvBlockH) is computed from this number, so changing it here
         silently changes how much room the grid thinks it has. */
      font-weight: 500;
      color: #ffffff;
      -webkit-text-stroke: 0.3px rgba(255,255,255,0.5);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      line-height: 1.3;
      white-space: nowrap;
    }
  `;
  }, [
    chrome,
    linePad,
    slideContentGap,
    slideContentSpacedGap,
    isPortrait,
    isTunedMobileLandscape,
    isTabletPortrait,
    isStandardMobilePortrait,
    isSmallMobilePortrait,
    layoutDevice,
    isTunedShellPortrait,
    shellContentWidth,
    arrowSafeZone,
    separatorLineWidth,
    separatorHeight,
    closeButtonSize,
    closeButtonRight,
    titleSideReserve,
    separatorMarginTop,
    separatorMarginBottom,
    headerPaddingBottom,
    headerTitleFontSize,
    slideTitleFontSize,
    featureSubtitleFontSize,
    glowTop,
    glowWidth,
  ]);

  if (!shouldRender) return null;

  const closeIconSrc = getUITextureAsDataURL("button_x");

  return (
    <div
      ref={overlayRef}
      className="spt-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        // Visible-area size (excludes browser toolbars). 100vw/100vh on iOS
        // Chrome resolve to the taller layout viewport, which pushes the
        // footer/pagination behind the bottom toolbar. dimensions.* come from
        // visualViewport (the real visible area) and match the fit math used
        // everywhere else in the overlay.
        width: dimensions.width,
        height: dimensions.height,
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.97) 100%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        padding: `${shellTopPad}px 0 ${shellBottomPad}px 0`,
        boxSizing: "border-box",
        opacity: 0,
        // When the iOS fullscreen overlay is up, hide the paytable for real:
        // opacity alone leaves the subtree hit-testable (the active slide sets
        // pointer-events:auto, defeating the root's `none`), so it sits on top of
        // the canvas and swallows the fullscreen swipe. visibility:hidden can't be
        // overridden by a descendant, so the whole subtree stops hit-testing.
        // visibility: isHidden ? "hidden" : "visible",
        // pointerEvents: isHidden ? "none" : "auto",
        // ...and while it is closed (kept mounted, 2026-10-30).
        visibility: isHidden || !isOpen ? "hidden" : "visible",
        pointerEvents: isHidden || !isOpen ? "none" : "auto",
      }}
    >
      {/* ── Decorative glow — standard mobile portrait only ── */}
      {isTunedStandardPortrait && (
        <img
          className="spt-glow"
          src={gameImages.glow || ""}
          alt=""
          aria-hidden="true"
        />
      )}

      {/* ── HEADER: title + close + separator ── */}
      <div className="spt-header">
        <div className="spt-header-title-row">
          {/* <h1 className="spt-title">
            {t("paytable_title")}
          </h1> */}
          <h1 className="spt-title" ref={titleRef}>
            <span
              className="spt-title-text"
              ref={titleTextRef}
              style={{ transform: `scale(${titleScale})` }}
            >
              {t("paytable_title")}
            </span>
          </h1>
          <button
            className="spt-close-btn"
            onClick={handleClose}
            style={{
              backgroundImage: closeIconSrc ? `url('${closeIconSrc}')` : "none",
            }}
          />
        </div>
        <div className="spt-separator" />
      </div>

      {/* ── CAROUSEL ── */}
      <PaytableSlider
        slides={slides}
        dimensions={dimensions}
        device={layoutDevice}
        orientation={orientation}
        chrome={chrome}
        toolbar={toolbar}
        isPortrait={isPortrait}
        // shouldRender={shouldRender}
        // The slider resets to slide 1 on OPEN — it is mounted all the time now.
        shouldRender={isOpen}
        contentWidth={
          isTabletPortrait ||
          isSmallMobilePortrait ||
          isStandardMobilePortrait ||
          isTunedMobileLandscape
            ? shellContentWidth
            : undefined
        }
        landscapeChromeSideOffset={tunedLandscapeChromeSideOffset}
        isTunedMobileLandscape={isTunedMobileLandscape}
        isWideDesktop={isWideDesktop}
        footerMarginTop={footerMarginTop}
        glowSrc={gameImages.glow}
        isHidden={isHidden}
      />

      {/* Overlay-scoped styles */}
      <style>{overlayStyles}</style>
    </div>
  );
};

export default PaytableOverlay;
